import { toast } from 'sonner';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useAdminDelete } from '../hooks/useAdminDelete';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';
import {
  Plus, Search, Printer, CheckCircle2, XCircle, Eye, Download, Edit2,
  ChevronRight, FileText, ArrowUpRight, ArrowDownRight,
  RotateCcw, Filter, Calendar, Building2, Truck, X,
  FlaskConical, Wrench, Package, IndianRupee, Users, BookOpen, Trash2, CreditCard
} from 'lucide-react';
import InvoiceModal from '../components/InvoiceModal';
import { BillAllocator } from '../components/ledger/SettlementEditor';
import { BillPrintPreview } from '../components/BillPrintPreview';
import AddToPriceListButton from '../components/AddToPriceListButton';
import { BatchPicker, BatchChips } from '../components/vouchers/BatchPicker';
import { IntegrityBadge } from '../components/vouchers/IntegrityBadge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const today = () => new Date().toISOString().slice(0, 10);
// Indian Financial Year start: April 1 of the current FY.  Jan-Mar fall in
// the PREVIOUS FY, so subtract one from the calendar year for those months.
const fyStart = () => {
  const d = new Date();
  const fyYear = d.getMonth() < 3 ? d.getFullYear() - 1 : d.getFullYear();
  return `${fyYear}-04-01`;
};

const downloadPdf = async (url, filename) => {
  // fetch blob → <a download>. Works reliably inside iframe/sandbox.
  try {
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || 'document.pdf';
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { a.remove(); window.URL.revokeObjectURL(blobUrl); }, 300);
    toast.success('Download started');
  } catch (e) { toast.error('Download failed — ' + (e.message || 'retry')); }
};

const printPdf = (url) => {
  const w = window.open(url, '_blank', 'noopener');
  if (!w) toast.info('Allow pop-ups and retry');
};

// ─── Voucher Category Config ───────────────────────────────────────────────
const CATEGORIES = [
  {
    id: 'accounting', name: 'Accounting Vouchers', icon: IndianRupee,
    color: 'text-blue-400', border: 'border-blue-800', bg: 'bg-blue-950/30',
    types: [
      { key: 'payment',  label: 'Payment',  prefix: 'PAY', hint: 'F5', desc: 'Record cash/bank payments to vendors, expenses' },
      { key: 'receipt',  label: 'Receipt',  prefix: 'REC', hint: 'F6', desc: 'Record money received from clients' },
      { key: 'journal',  label: 'Journal',  prefix: 'JNL', hint: 'F7', desc: 'General ledger adjustments & corrections' },
      { key: 'contra',   label: 'Contra',   prefix: 'CON', hint: 'F4', desc: 'Cash ↔ Bank transfers' },
    ],
  },
  {
    id: 'trading', name: 'Trading Vouchers', icon: Building2,
    color: 'text-green-400', border: 'border-green-800', bg: 'bg-green-950/30',
    types: [
      { key: 'sales_invoice',    label: 'Sales Invoice',    prefix: 'INV', hint: 'F8', desc: 'Invoice for goods sold to clients + GST' },
      { key: 'purchase_invoice', label: 'Purchase Invoice', prefix: 'PUR', hint: 'F9', desc: 'Invoice received from vendors + GST input' },
      { key: 'credit_note',      label: 'Credit Note',      prefix: 'CRN', hint: '',   desc: 'Sales return — credit back to client' },
      { key: 'debit_note',       label: 'Debit Note',       prefix: 'DBN', hint: '',   desc: 'Purchase return — debit back to vendor' },
    ],
  },
  {
    id: 'inventory', name: 'Inventory Vouchers', icon: Package,
    color: 'text-amber-400', border: 'border-amber-800', bg: 'bg-amber-950/30',
    types: [
      { key: 'material_inward',  label: 'Material Inward (GRN)',  prefix: 'GRN', hint: '', desc: 'Goods Receipt Note — raw material received from vendor' },
      { key: 'material_outward', label: 'Material Outward (MIV)', prefix: 'MIV', hint: '', desc: 'Material Issue Voucher — issue to production' },
      { key: 'fg_inward',        label: 'FG Inward',              prefix: 'FGI', hint: '', desc: 'Finished goods added after production' },
      { key: 'fg_outward',       label: 'FG Outward (Dispatch)',  prefix: 'FGO', hint: '', desc: 'Dispatch to client from finished goods' },
      { key: 'sample_issue',     label: 'Sample Issue',           prefix: 'SMP', hint: '', desc: 'Sample given to client / exhibition / testing' },
      { key: 'rejection',        label: 'Rejection / Damage',     prefix: 'REJ', hint: '', desc: 'Write off damaged or rejected material/goods' },
      { key: 'stock_transfer',   label: 'Stock Transfer',         prefix: 'STR', hint: '', desc: 'Move stock between storage locations' },
    ],
  },
  {
    id: 'production', name: 'Production Vouchers', icon: FlaskConical,
    color: 'text-purple-400', border: 'border-purple-800', bg: 'bg-purple-950/30',
    types: [
      { key: 'production_order', label: 'Production Order',   prefix: 'PRD', hint: '', desc: 'Authorize batch production with material allocation' },
      { key: 'wastage',          label: 'Wastage Voucher',    prefix: 'WST', hint: '', desc: 'Record production wastage and process loss' },
      { key: 'qc_record',        label: 'QC Inspection',      prefix: 'QCR', hint: '', desc: 'Quality check results for finished batch' },
      { key: 'rework',           label: 'Rework Voucher',     prefix: 'RWK', hint: '', desc: 'Batch sent back for rework / reprocessing' },
    ],
  },
  {
    id: 'maintenance', name: 'Maintenance', icon: Wrench,
    color: 'text-orange-400', border: 'border-orange-800', bg: 'bg-orange-950/30',
    types: [
      { key: 'maintenance', label: 'Machine Maintenance', prefix: 'MNT', hint: '', desc: 'Preventive/breakdown maintenance record + cost' },
    ],
  },
];

const ALL_TYPES = CATEGORIES.flatMap(c => c.types.map(t => ({ ...t, category: c.id, categoryName: c.name })));

const STATUS_STYLE = {
  approved: 'bg-green-900/40 text-green-300 border-green-700',
  draft: 'bg-zinc-800 text-zinc-400 border-zinc-700',
  cancelled: 'bg-red-900/40 text-red-300 border-red-700',
};
const TYPE_COLOR = {
  accounting: 'text-blue-400', trading: 'text-green-400',
  inventory: 'text-amber-400', production: 'text-purple-400', maintenance: 'text-orange-400',
};

// ─── Create Voucher Form fields by type ───────────────────────────────────
//
// `existingVoucher` (optional) — when set, the form preloads its fields and
// the submit calls PUT instead of POST, allowing an admin/manager to fix
// items, rates, parties, or extra charges on an already-saved voucher.
function VoucherForm({ type, vendors, materials, products, batches, employees, machines, clients, onSubmit, onClose, existingVoucher = null, onProductsChanged }) {
  const today_str = today();
  const [form, setForm] = useState(() => existingVoucher
    ? {
        ...existingVoucher,
        gst_enabled: !!existingVoucher.gst_enabled,
        // Purchase bills keep transport as an extra charge — surface it in the Transport Exp box
        // so a re-save doesn't silently drop it (backend maps it back to the "Transport" charge).
        transport_exp: parseFloat(existingVoucher.transport_exp || 0)
          || (existingVoucher.extra_charges || []).filter(c => /transport/i.test(c.label || '')).reduce((s, c) => s + (parseFloat(c.amount) || 0), 0),
      }
    : { date: today_str, narration: '', party_name: '', party_id: '', amount: 0, payment_mode: 'Bank', status: 'approved', voucher_number: '', gst_enabled: true });
  const [items, setItems] = useState(() => {
    if (existingVoucher && (existingVoucher.items || []).length > 0) {
      // Vouchers mirrored from purchase bills carry names only — resolve ids so the selects show the line
      return existingVoucher.items.map(it => {
        if (it.material_id || it.product_id) return it;
        const nm = (it.material_name || it.product_name || '').trim().toLowerCase();
        const m = (materials || []).find(x => (x.name || '').trim().toLowerCase() === nm);
        const p = !m && (products || []).find(x => (x.name || '').trim().toLowerCase() === nm);
        return { ...it, material_id: m?.id || '', product_id: p?.id || '' };
      });
    }
    return [{ material_id: '', material_name: '', product_id: '', product_name: '', quantity: 1, rate: 0, unit: 'kg', gst_rate: 5, lot_no: '', quality: 'Good', mfg_date: '', expiry_date: '' }];
  });
  const [entries, setEntries] = useState(() => (existingVoucher?.entries || [{ account: '', debit: 0, credit: 0 }]));
  const [qcParams, setQcParams] = useState(() => existingVoucher?.qc_parameters || [
    { name: 'Moisture Content', standard: '≤12%', actual: '', result: 'Pass' },
    { name: 'Foreign Matter', standard: '≤0.5%', actual: '', result: 'Pass' },
    { name: 'Packaging Integrity', standard: 'Intact', actual: '', result: 'Pass' },
    { name: 'Colour & Appearance', standard: 'Uniform', actual: '', result: 'Pass' },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [clientPriceList, setClientPriceList] = useState([]);
  // Receipt / Payment: open bills of the party so the operator can pick which
  // invoices this money settles (Tally "Agst Ref"). Backend: /settlements.
  const isMoneyVoucher = ['receipt', 'payment'].includes(type.key);
  // Credit / Debit note: a return is a settlement without cash — it is adjusted
  // against the party's open bills exactly like a receipt / payment.
  const isNoteVoucher = ['credit_note', 'debit_note'].includes(type.key);
  const [openBills, setOpenBills] = useState([]);
  const [alloc, setAlloc] = useState(() => {
    const a = {};
    (existingVoucher?.against_bills || []).forEach(ab => { if (ab.bill_id && ab.amount > 0) a[ab.bill_id] = String(ab.amount); });
    return a;
  });
  useEffect(() => {
    if (!(isMoneyVoucher || isNoteVoucher) || !form.party_id || (type.key === 'payment' && form.party_role === 'debtor')) { setOpenBills([]); return; }
    const side = ['receipt', 'credit_note'].includes(type.key) ? 'debtor' : 'creditor';
    axios.get(`${API}/settlements/open-bills`, { params: { client_id: form.party_id, side, voucher_id: existingVoucher?.id || '' }, withCredentials: true })
      .then(r => setOpenBills(r.data.bills || []))
      .catch(() => setOpenBills([]));
  }, [isMoneyVoucher, isNoteVoucher, form.party_id, form.party_role, type.key, existingVoucher]);

  // Fetch auto-generated voucher number — only for NEW vouchers; on edit the
  // voucher number is immutable and already on the form.
  useEffect(() => {
    if (existingVoucher) return;
    axios.get(`${API}/vouchers/next-number/${type.key}`, { withCredentials: true })
      .then(r => setForm(f => ({ ...f, voucher_number: f.voucher_number || r.data.voucher_number })))
      .catch(() => {});
  }, [type.key, existingVoucher]);

  // Determine which client type to show
  const payToCustomer = type.key === 'payment' && form.party_role === 'debtor';
  const isDebtorVoucher = ['receipt', 'sales_invoice', 'credit_note', 'fg_outward', 'sample_issue'].includes(type.key) || payToCustomer;
  const isCreditorVoucher = ['payment', 'purchase_invoice', 'debit_note'].includes(type.key) && !payToCustomer;
  const partyClients = clients.filter(c => {
    if (isDebtorVoucher) return c.type === 'Debtor' || c.type === 'Both';
    if (isCreditorVoucher) return c.type === 'Creditor' || c.type === 'Both';
    return true;
  });

  const selectParty = (clientId) => {
    const c = clients.find(cl => cl.id === clientId);
    if (c) {
      const hasGstin = !!(c.gstin && c.gstin.trim());
      setForm(f => ({
        ...f,
        party_name: c.name,
        party_id: c.id,
        gst_enabled: type.key === 'sales_invoice' ? (hasGstin ? f.gst_enabled : false) : hasGstin,
        client_address: [c.address, c.city, c.state].filter(Boolean).join(', '),
        client_phone: c.phone || '',
        client_gstin: c.gstin || '',
        client_state: c.state || 'Maharashtra',
        payment_terms: c.payment_terms || f.payment_terms || '',
      }));
      // Fetch price list for sales vouchers
      if (['sales_invoice', 'credit_note'].includes(type.key)) {
        axios.get(`${API}/clients/${c.id}/price-list`, { withCredentials: true })
          .then(r => setClientPriceList(r.data || []))
          .catch(() => setClientPriceList([]));
      }
    }
  };

  const setProductForItem = (idx, productId) => {
    const p = products.find(pr => pr.id === productId);
    const priceEntry = clientPriceList.find(pl => pl.product_id === productId);
    setItems(prev => {
      const n = [...prev];
      let rate = p?.price_per_unit || 0;
      if (priceEntry && priceEntry.rate > 0) {
        rate = priceEntry.rate;
        if (priceEntry.discount_pct > 0) rate = rate * (1 - priceEntry.discount_pct / 100);
        if (priceEntry.markup_pct > 0) rate = rate * (1 + priceEntry.markup_pct / 100);
        rate = Math.round(rate * 100) / 100;
      }
      // Clear material_id when picking a finished good — keeps the backend
      // entry clean (it expects exactly one of product_id / material_id).
      n[idx] = { ...n[idx], product_id: productId, material_id: '', material_name: '', product_name: p?.name || '', rate, gst_rate: p?.gst_rate || 5, unit: priceEntry?.unit || p?.unit || n[idx].unit };
      return n;
    });
  };

  // Pick a RAW MATERIAL inside a sales voucher (some clients buy raw goods
  // direct from stock — e.g. bulk turmeric, kg-pack ingredients).  Mirrors
  // setProductForItem but stores material_id + name and uses:
  //   1. The client's custom price-list entry (matched by material_id) if any
  //   2. Otherwise the material's last-purchase rate as the suggested price
  // iter202 — bug fix: previously RM rows always ignored the price list,
  // even when the debtor had a saved RM rate.
  const setMaterialForSalesItem = (idx, materialId) => {
    const m = materials.find(mt => mt.id === materialId);
    const priceEntry = clientPriceList.find(pl => pl.product_id === materialId);
    setItems(prev => {
      const n = [...prev];
      let rate = parseFloat(m?.cost_per_unit || m?.last_price || m?.current_rate || 0) || 0;
      if (priceEntry && priceEntry.rate > 0) {
        rate = priceEntry.rate;
        if (priceEntry.discount_pct > 0) rate = rate * (1 - priceEntry.discount_pct / 100);
        if (priceEntry.markup_pct > 0)   rate = rate * (1 + priceEntry.markup_pct / 100);
        rate = Math.round(rate * 100) / 100;
      }
      n[idx] = {
        ...n[idx],
        material_id: materialId,
        material_name: m?.name || '',
        product_id: '',
        product_name: '',
        rate,
        gst_rate: parseFloat(m?.gst_rate ?? 5),
        hsn_code: m?.hsn_code || n[idx].hsn_code || '',
        unit: priceEntry?.unit || m?.unit || n[idx].unit || 'kg',
        has_custom_price: !!priceEntry,
      };
      return n;
    });
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const addItem = () => setItems(i => [...i, { material_id: '', material_name: '', product_id: '', product_name: '', quantity: 1, rate: 0, unit: 'kg', gst_rate: 5, lot_no: '', quality: 'Good', mfg_date: '', expiry_date: '' }]);
  const removeItem = (i) => setItems(prev => prev.filter((_, idx) => idx !== i));
  const setItem = (i, k, v) => setItems(prev => { const n = [...prev]; n[i] = { ...n[i], [k]: v }; return n; });
  const addEntry = () => setEntries(e => [...e, { account: '', debit: 0, credit: 0 }]);
  const setEntry = (i, k, v) => setEntries(prev => { const n = [...prev]; n[i] = { ...n[i], [k]: v }; return n; });

  const totalDebit = entries.reduce((s, e) => s + parseFloat(e.debit || 0), 0);
  const totalCredit = entries.reduce((s, e) => s + parseFloat(e.credit || 0), 0);
  const itemSubtotal = items.reduce((s, i) => s + (parseFloat(i.quantity || 0) * parseFloat(i.rate || 0)), 0);
  const itemGst = form.gst_enabled ? items.reduce((s, i) => s + (parseFloat(i.quantity || 0) * parseFloat(i.rate || 0) * parseFloat(i.gst_rate || 0) / 100), 0) : 0;
  const itemTotal = itemSubtotal + itemGst;
  // iter211 — Cash / settlement discount (subtracted after GST + extras). Same formula as backend compute_totals, incl. Tally round-off.
  const discountAmount = parseFloat(form.discount_amount || 0) || 0;
  const otherExtras = (form.extra_charges || []).filter(c => !/transport/i.test(c.label || '')).reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const grossTotal = (form.gst_enabled ? itemTotal : itemSubtotal) + parseFloat(form.transport_exp || 0) + otherExtras;
  const rawNet = Math.max(0, grossTotal - discountAmount);
  const netTotal = Math.floor(rawNet + 0.5);
  const roundOff = Math.round((netTotal - rawNet) * 100) / 100;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const allocations = Object.entries(alloc).filter(([, v]) => parseFloat(v) > 0).map(([bill_id, v]) => ({ bill_id, amount: parseFloat(v) }));
    const taggedItems = ['sales_invoice', 'credit_note'].includes(type.key)
      ? items.map(it => ({ ...it, item_type: it.item_type || (it.product_id ? 'finished' : it.material_id ? 'raw' : 'finished') }))
      : items;
    const payload = {
      ...form,
      voucher_type: type.key,
      voucher_number: form.voucher_number || '',
      ...(type.key === 'sales_invoice' ? { invoice_number: (form.invoice_number || '').trim(), bill_no_override: existingVoucher ? (form.bill_no_override || '') : (form.invoice_number || '').trim() } : {}),
      items: ['payment', 'receipt', 'contra', 'journal'].includes(type.key) ? [] : taggedItems,
      entries,
      ...(isMoneyVoucher ? {
        against_bills: allocations,
        allocations,
        receipt_type: allocations.length ? 'against_bill' : 'on_account',
        payment_type: allocations.length ? 'against_bill' : 'on_account',
      } : {}),
      ...(isNoteVoucher ? { against_bills: allocations, restock: form.restock !== false } : {}),
      qc_parameters: qcParams,
      amount: ['payment', 'receipt', 'contra'].includes(type.key) ? parseFloat(form.amount) : netTotal,
      gst_rate: form.gst_enabled ? (items[0]?.gst_rate || 5) : 0,
      transport_exp: parseFloat(form.transport_exp || 0),
      discount_amount: discountAmount,
      discount_reason: form.discount_reason || '',
      machine_id: form.machine_id,
      machine_name: form.machine_name,
      maintenance_type: form.maintenance_type,
      maintenance_cost: parseFloat(form.maintenance_cost || 0),
      next_maintenance_date: form.next_maintenance_date,
      done_by: form.done_by,
      batch_id: form.batch_id,
      qc_result: form.qc_result || (qcParams.every(p => p.result === 'Pass') ? 'Pass' : 'Fail'),
      wastage_type: form.wastage_type,
      loss_percentage: parseFloat(form.loss_percentage || 0),
    };
    try { await onSubmit(payload); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error creating voucher'); }
    finally { setSubmitting(false); }
  };

  // Tally color scheme per voucher category
  const tallyTheme = (() => {
    if (['sales_invoice', 'credit_note', 'fg_outward', 'sample_issue'].includes(type.key)) return { bg: 'bg-amber-50', header: 'bg-[#1a237e]', text: 'text-gray-900', input: 'bg-white border-gray-300 text-gray-900', label: 'text-gray-700', narr: 'bg-yellow-200', accent: 'text-[#1a237e]', tableBg: 'bg-white', tableHead: 'bg-blue-100 text-gray-800', endList: 'text-gray-500 bg-yellow-100' };
    if (['purchase_invoice', 'debit_note', 'material_inward'].includes(type.key)) return { bg: 'bg-red-50', header: 'bg-[#1a237e]', text: 'text-gray-900', input: 'bg-white border-gray-300 text-gray-900', label: 'text-gray-700', narr: 'bg-red-200', accent: 'text-[#b71c1c]', tableBg: 'bg-white', tableHead: 'bg-red-100 text-gray-800', endList: 'text-gray-500 bg-red-50' };
    if (['payment', 'receipt', 'contra'].includes(type.key)) return { bg: 'bg-[#FFF8E1]', header: 'bg-[#1a237e]', text: 'text-gray-900', input: 'bg-white border-gray-300 text-gray-900', label: 'text-gray-700', narr: 'bg-yellow-200', accent: 'text-[#1a237e]', tableBg: 'bg-white', tableHead: 'bg-amber-100 text-gray-800', endList: 'text-gray-500 bg-amber-50' };
    return { bg: 'bg-gray-50', header: 'bg-[#1a237e]', text: 'text-gray-900', input: 'bg-white border-gray-300 text-gray-900', label: 'text-gray-700', narr: 'bg-blue-100', accent: 'text-[#1a237e]', tableBg: 'bg-white', tableHead: 'bg-gray-200 text-gray-800', endList: 'text-gray-500 bg-gray-100' };
  })();

  const inputCls = `w-full ${tallyTheme.input} border rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`;
  const labelCls = `${tallyTheme.label} text-xs mb-1 block font-medium`;

  // Keyboard navigation: Enter moves to next input
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA' && e.target.type !== 'submit') {
      e.preventDefault();
      const form = e.target.form;
      const inputs = Array.from(form.querySelectorAll('input:not([type="hidden"]),select,textarea'));
      const idx = inputs.indexOf(e.target);
      if (idx >= 0 && idx < inputs.length - 1) inputs[idx + 1].focus();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto p-2">
      <div className={`${tallyTheme.bg} border border-gray-400 rounded w-full max-w-3xl my-2 shadow-xl`} style={{ fontFamily: "'Segoe UI', 'Consolas', monospace" }}>
        {/* Tally-style Blue Header */}
        <div className={`${tallyTheme.header} px-4 py-2 flex items-center justify-between`}>
          <div>
            <h2 className="font-bold text-white text-base tracking-wide">{existingVoucher ? `Edit ${type.label}` : type.label}</h2>
            <p className="text-blue-200 text-[10px]">{type.desc}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-blue-200 text-[10px] font-mono">F2: Date | Tab: Next | Enter: Accept</span>
            <button onClick={onClose} className="text-blue-200 hover:text-white"><X size={18} /></button>
          </div>
        </div>

        <form onSubmit={handleSubmit} onKeyDown={handleKeyDown} className="p-4 space-y-3">
          {/* Common top fields - Tally style */}
          <div className={`grid grid-cols-1 ${type.key === 'sales_invoice' ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} gap-2 border-b border-gray-300 pb-3`}>
            <div>
              <label className={labelCls}>Voucher Date *</label>
              <input type="date" value={form.date} onChange={e => set('date', e.target.value)} className={`${inputCls} font-mono`} required />
            </div>
            <div>
              <label className={labelCls}>Voucher No.</label>
              <input value={form.voucher_number} onChange={e => set('voucher_number', e.target.value)} placeholder="Auto-generated" className={`${inputCls} font-mono`} data-testid="voucher-number-input" />
            </div>
            {type.key === 'sales_invoice' && (
              <div>
                <label className={labelCls}>Invoice / Bill No. {existingVoucher ? '(ledger & Bill Log)' : ''}</label>
                <input value={form.invoice_number || ''} onChange={e => set('invoice_number', e.target.value)}
                  placeholder={form.gst_enabled !== false ? 'Auto TI/GST/26-27/NNN' : 'Auto TE/26-27/NNN'} className={`${inputCls} font-mono`} data-testid="invoice-number-input" />
              </div>
            )}
            <div>
              <label className={labelCls}>Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)} className={inputCls}>
                <option value="approved">Approved</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>

          {/* ── PAYMENT / RECEIPT ─────────────────────── */}
          {['payment', 'receipt'].includes(type.key) && (
            <div className="space-y-3">
              {type.key === 'payment' && (
                <div className="flex flex-wrap items-center gap-2" data-testid="payment-party-role">
                  <span className={labelCls}>Pay to</span>
                  {[['creditor', 'Vendor (against bills)'], ['debtor', 'Customer (refund / loan)']].map(([k, l]) => (
                    <button key={k} type="button" data-testid={`payment-role-${k}`}
                      onClick={() => setForm(f => ({ ...f, party_role: k, party_id: '', party_name: '', payment_purpose: k === 'debtor' ? (f.payment_purpose || 'Refund') : '' }))}
                      className={`text-[11px] px-2.5 py-1 rounded-full border ${(form.party_role || 'creditor') === k ? 'bg-amber-500 text-black border-amber-500 font-semibold' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'}`}>
                      {l}
                    </button>
                  ))}
                  {payToCustomer && (
                    <select value={form.payment_purpose || 'Refund'} onChange={e => set('payment_purpose', e.target.value)} data-testid="payment-purpose"
                      className="text-[11px] bg-white text-gray-800 border border-gray-300 rounded px-2 py-1">
                      {['Refund', 'Loan given', 'Advance returned', 'Other'].map(p => <option key={p}>{p}</option>)}
                    </select>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelCls}>{type.key === 'payment' ? (payToCustomer ? 'Pay To (Customer)' : 'Pay To (Creditor)') : 'Received From (Debtor)'} *
                    {clientPriceList.length > 0 && <span className="ml-2 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{clientPriceList.length} prices</span>}
                  </label>
                  <select value={form.party_id || ''} onChange={e => selectParty(e.target.value)} className={inputCls} data-testid="party-select">
                    <option value="">-- Select {isDebtorVoucher ? 'Debtor' : 'Creditor'} --</option>
                    {partyClients.map(c => <option key={c.id} value={c.id}>{c.name}{c.gstin ? ` (${c.gstin})` : ''}</option>)}
                  </select>
                  <input value={form.party_name} onChange={e => set('party_name', e.target.value)} required className={`${inputCls} mt-1`} placeholder="Or type name manually" />
                </div>
                <div>
                  <label className={labelCls}>Amount (Rs.) *</label>
                  <input type="number" step="any" value={form.amount} onChange={e => set('amount', e.target.value)} required className={`${inputCls} font-mono text-lg`} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <label className={labelCls}>Payment Mode</label>
                  <select value={form.payment_mode} onChange={e => set('payment_mode', e.target.value)} className={inputCls}>
                    {['Cash','Bank (NEFT)','Bank (RTGS)','Bank (Cheque)','UPI','DD'].map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Cheque / Ref No.</label>
                  <input value={form.cheque_no || ''} onChange={e => set('cheque_no', e.target.value)} className={inputCls} placeholder="Optional" />
                </div>
                <div>
                  <label className={labelCls}>Bank Account</label>
                  <input value={form.bank_account || ''} onChange={e => set('bank_account', e.target.value)} className={inputCls} placeholder="HDFC / SBI..." />
                </div>
              </div>
              {/* Bill-wise allocation (Agst Ref) — receipt AND payment (not for money paid to a customer) */}
              {payToCustomer ? (
                <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="customer-payment-note">
                  Booked as <b>Payment Made to Debtor</b> (debit) on {form.party_name || 'the customer'}'s ledger ({form.payment_purpose || 'Refund'}) — it increases what they owe you / reduces their credit. In their Cash flow it is added to that month's Sales, marked "payment made to debtor".
                </div>
              ) : form.party_id ? (
                <div className="border border-gray-300 rounded p-2 bg-white" data-testid="voucher-bill-allocator">
                  <div className="text-gray-700 text-[10px] uppercase tracking-wider font-bold mb-1.5">
                    {type.key === 'receipt' ? 'Received against invoices' : 'Paid against bills'}
                  </div>
                  <BillAllocator bills={openBills} alloc={alloc} setAlloc={setAlloc} amount={form.amount} side={type.key === 'receipt' ? 'debtor' : 'creditor'} testPrefix="voucher-alloc" theme="light" />
                </div>
              ) : (
                <div className="text-[11px] text-gray-500 italic">Select a party to see their open {type.key === 'receipt' ? 'invoices' : 'bills'} — leave unallocated to book on account.</div>
              )}
              {/* Tally-style Double Entry Preview */}
              <div className={`${tallyTheme.tableBg} border border-gray-300 rounded overflow-x-auto`}>
                <div className={`${tallyTheme.tableHead} px-3 py-1.5 text-xs font-bold border-b border-gray-300`}>Accounting Entry (Auto)</div>
                <div className="px-3 py-2 text-sm font-mono space-y-1">
                  {type.key === 'payment' ? (
                    <>
                      <div className="flex justify-between"><span className="text-gray-900">Dr: {form.party_name || '[ Party Account ]'}</span><span className="text-red-700 font-bold">{fmt(form.amount)}</span></div>
                      <div className="flex justify-between pl-6"><span className="text-gray-700">Cr: {form.payment_mode?.includes('Cash') ? 'Cash A/c' : 'Bank A/c'}</span><span className="text-green-700 font-bold">{fmt(form.amount)}</span></div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between"><span className="text-gray-900">Dr: {form.payment_mode?.includes('Cash') ? 'Cash A/c' : 'Bank A/c'}</span><span className="text-red-700 font-bold">{fmt(form.amount)}</span></div>
                      <div className="flex justify-between pl-6"><span className="text-gray-700">Cr: {form.party_name || '[ Party Account ]'}</span><span className="text-green-700 font-bold">{fmt(form.amount)}</span></div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── CONTRA ─────────────────────── */}
          {type.key === 'contra' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <label className={labelCls}>From Account</label>
                <select value={form.from_account || 'Cash'} onChange={e => set('from_account', e.target.value)} className={inputCls}>
                  {['Cash', 'HDFC Bank', 'SBI Bank', 'Petty Cash'].map(a => <option key={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>To Account</label>
                <select value={form.to_account || 'HDFC Bank'} onChange={e => set('to_account', e.target.value)} className={inputCls}>
                  {['Cash', 'HDFC Bank', 'SBI Bank', 'Petty Cash'].map(a => <option key={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Amount (Rs.) *</label>
                <input type="number" required value={form.amount} onChange={e => set('amount', e.target.value)} className={`${inputCls} font-mono`} />
              </div>
            </div>
          )}

          {/* ── JOURNAL ─────────────────────── */}
          {type.key === 'journal' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-1">
                <label className={`${tallyTheme.accent} text-sm font-bold`}>Journal Entries</label>
                <button type="button" onClick={addEntry} className="text-xs text-blue-700 flex items-center gap-1 hover:text-blue-900"><Plus size={12}/> Add Row</button>
              </div>
              <div className={`${tallyTheme.tableBg} border border-gray-300 rounded overflow-x-auto`}>
                <table className="w-full text-xs min-w-[520px]">
                  <thead><tr className={`${tallyTheme.tableHead} border-b border-gray-300`}>
                    <th className="text-left px-3 py-2 font-bold">Particulars</th>
                    <th className="text-right px-3 py-2 font-bold">Debit (Rs.)</th>
                    <th className="text-right px-3 py-2 font-bold">Credit (Rs.)</th>
                    <th className="px-2 py-2 w-8"></th>
                  </tr></thead>
                  <tbody>
                    {entries.map((en, i) => (
                      <tr key={i} className="border-b border-gray-200">
                        <td className="px-2 py-1"><input value={en.account} onChange={e => setEntry(i,'account',e.target.value)} placeholder="Account name" className="w-full bg-transparent text-gray-900 focus:outline-none" /></td>
                        <td className="px-2 py-1"><input type="number" value={en.debit} onChange={e => setEntry(i,'debit',e.target.value)} className="w-full bg-transparent text-right text-gray-900 focus:outline-none font-mono" /></td>
                        <td className="px-2 py-1"><input type="number" value={en.credit} onChange={e => setEntry(i,'credit',e.target.value)} className="w-full bg-transparent text-right text-gray-900 focus:outline-none font-mono" /></td>
                        <td className="px-2 py-1"><button type="button" onClick={() => setEntries(p => p.filter((_,j)=>j!==i))}><X size={12} className="text-gray-400 hover:text-red-600"/></button></td>
                      </tr>
                    ))}
                    <tr className={tallyTheme.endList}><td colSpan={4} className="text-center text-[10px] py-1 italic">--- End of List ---</td></tr>
                  </tbody>
                  <tfoot><tr className={`border-t-2 border-gray-400 text-xs font-bold ${Math.abs(totalDebit-totalCredit)<0.01 ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'}`}>
                    <td className="px-3 py-2">{Math.abs(totalDebit-totalCredit)<0.01 ? 'Balanced' : 'Not Balanced'}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(totalDebit)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(totalCredit)}</td>
                    <td/>
                  </tr></tfoot>
                </table>
              </div>
            </div>
          )}

          {/* ── SALES / PURCHASE INVOICE ─────────────────────── */}
          {['sales_invoice','purchase_invoice','credit_note','debit_note'].includes(type.key) && (
            <div className="space-y-3">
              {/* GST/Non-GST chooser for sales_invoice */}
              {type.key === 'sales_invoice' && (
                <div className="flex items-center gap-3 border border-gray-300 rounded p-2">
                  <span className={`${labelCls} mb-0`}>Billing Entity:</span>
                  <button type="button" onClick={() => set('gst_enabled', true)}
                    className={`px-3 py-1.5 rounded text-xs font-bold border transition-all ${form.gst_enabled ? 'bg-green-100 border-green-500 text-green-800' : 'bg-gray-100 border-gray-300 text-gray-500'}`}
                    data-testid="voucher-choose-gst">
                    Tirth Industries (GST)
                  </button>
                  <button type="button" onClick={() => set('gst_enabled', false)}
                    className={`px-3 py-1.5 rounded text-xs font-bold border transition-all ${!form.gst_enabled ? 'bg-amber-100 border-amber-500 text-amber-800' : 'bg-gray-100 border-gray-300 text-gray-500'}`}
                    data-testid="voucher-choose-nongst">
                    Tirth Enterprises (Non-GST)
                  </button>
                </div>
              )}

              {/* Debtor/Creditor selector */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelCls}>{['sales_invoice','credit_note'].includes(type.key) ? 'Client (Debtor)' : 'Vendor (Creditor)'} *
                    {clientPriceList.length > 0 && <span className="ml-2 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{clientPriceList.length} custom prices</span>}
                  </label>
                  <select value={form.party_id || ''} onChange={e => selectParty(e.target.value)} className={inputCls} data-testid="party-select">
                    <option value="">-- Select {['sales_invoice','credit_note'].includes(type.key) ? 'Debtor' : 'Creditor'} --</option>
                    {partyClients.map(c => <option key={c.id} value={c.id}>{c.name}{c.gstin ? ` (${c.gstin})` : ''}</option>)}
                  </select>
                  <input value={form.party_name} onChange={e => set('party_name', e.target.value)} required className={`${inputCls} mt-1`} placeholder="Or type party name" />
                </div>
                <div className="space-y-1">
                  <label className={labelCls}>Client Details (auto-filled)</label>
                  <input value={form.client_address || ''} onChange={e => set('client_address', e.target.value)} className={inputCls} placeholder="Address" />
                  <div className="grid grid-cols-2 gap-1">
                    <input value={form.client_phone || ''} onChange={e => set('client_phone', e.target.value)} className={inputCls} placeholder="Phone" />
                    <input value={form.client_state || ''} onChange={e => set('client_state', e.target.value)} className={inputCls} placeholder="State" />
                  </div>
                  {form.gst_enabled && <input value={form.client_gstin || ''} onChange={e => set('client_gstin', e.target.value)} className={inputCls} placeholder="GSTIN/UIN" />}
                </div>
              </div>

              {/* Reference & Dispatch Details */}
              {type.key === 'sales_invoice' && (
                <div className="border border-gray-300 rounded p-2 space-y-2">
                  <span className="text-gray-500 text-[10px] uppercase tracking-wider font-bold">
                    {form.gst_enabled ? 'Transport & Dispatch' : 'Reference & Dispatch Details'}
                  </span>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div><label className={labelCls}>Dispatch Through</label><input value={form.dispatch_through || ''} onChange={e => set('dispatch_through', e.target.value)} className={inputCls} /></div>
                    <div><label className={labelCls}>Destination</label><input value={form.destination || ''} onChange={e => set('destination', e.target.value)} className={inputCls} /></div>
                    <div><label className={labelCls}>Vehicle No.</label><input value={form.vehicle_no || ''} onChange={e => set('vehicle_no', e.target.value)} className={inputCls} /></div>
                    <div><label className={labelCls}>Delivery Note / LR No.</label><input value={form.lr_no || ''} onChange={e => set('lr_no', e.target.value)} className={inputCls} /></div>
                    <div><label className={labelCls}>Buyer Ref / PO No.</label><input value={form.buyer_ref || ''} onChange={e => set('buyer_ref', e.target.value)} className={inputCls} /></div>
                    {form.gst_enabled ? (
                      <div><label className={labelCls}>e-Way Bill No.</label><input value={form.eway_bill_no || ''} onChange={e => set('eway_bill_no', e.target.value)} className={inputCls} /></div>
                    ) : (
                      <div><label className={labelCls}>Terms of Delivery</label><input value={form.terms_of_delivery || ''} onChange={e => set('terms_of_delivery', e.target.value)} className={inputCls} /></div>
                    )}
                  </div>
                  {!form.gst_enabled && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div><label className={labelCls}>Supplier's Ref.</label><input value={form.supplier_ref || ''} onChange={e => set('supplier_ref', e.target.value)} className={inputCls} /></div>
                      <div><label className={labelCls}>Other References</label><input value={form.other_ref || ''} onChange={e => set('other_ref', e.target.value)} className={inputCls} /></div>
                      <div><label className={labelCls}>Despatch No.</label><input value={form.dispatch_id || ''} onChange={e => set('dispatch_id', e.target.value)} className={inputCls} /></div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div><label className={labelCls}>Payment Terms</label><input value={form.payment_terms || ''} onChange={e => set('payment_terms', e.target.value)} className={inputCls} placeholder="Cash / Net 30" /></div>
                    <div><label className={labelCls}>Transport Exp (Rs.)</label><input type="number" min="0" value={form.transport_exp || ''} onChange={e => set('transport_exp', e.target.value)} className={inputCls} /></div>
                    <div><label className={labelCls}>Reference / PO No.</label><input value={form.reference_no||''} onChange={e => set('reference_no',e.target.value)} className={inputCls} /></div>
                  </div>
                </div>
              )}
              {type.key !== 'sales_invoice' && (
                <div className="grid grid-cols-2 gap-2">
                  <div><label className={labelCls}>Reference / PO No.</label><input value={form.reference_no||''} onChange={e => set('reference_no',e.target.value)} className={inputCls} placeholder="PO / SO reference" /></div>
                </div>
              )}

              {/* Purchase invoice: transport / freight (mirrors the bill's "Transport" extra charge) */}
              {type.key === 'purchase_invoice' && (
                <div className="border border-gray-300 rounded p-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <div><label className={labelCls}>Transport / Freight (Rs.)</label><input type="number" min="0" value={form.transport_exp || ''} onChange={e => set('transport_exp', e.target.value)} className={inputCls} data-testid="voucher-transport-exp" /></div>
                  <div><label className={labelCls}>Supplier Bill No.</label><input value={form.reference_no || ''} onChange={e => set('reference_no', e.target.value)} className={inputCls} data-testid="voucher-reference-no" /></div>
                  <div className="col-span-2 sm:col-span-1 self-end text-[10px] text-gray-500">Saving a purchase invoice also updates the purchase bill, creditor ledger and RM lots.</div>
                </div>
              )}

              {/* Tally-style Items table */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className={`${tallyTheme.accent} text-sm font-bold`}>Line Items</label>
                  <button type="button" onClick={addItem} className="text-xs text-blue-700 flex items-center gap-1 hover:text-blue-900"><Plus size={12}/> Add Item</button>
                </div>
                <div className={`${tallyTheme.tableBg} border border-gray-300 rounded overflow-x-auto`}>
                  <table className="w-full text-xs min-w-[520px]">
                    <thead><tr className={`${tallyTheme.tableHead} border-b border-gray-300`}>
                      <th className="text-left px-3 py-2 font-bold">Name of Item</th>
                      <th className="px-2 py-2 font-bold">Quantity</th>
                      <th className="px-2 py-2 font-bold">Unit</th>
                      <th className="px-2 py-2 font-bold">Rate per</th>
                      {form.gst_enabled && <th className="px-2 py-2 font-bold">GST %</th>}
                      <th className="text-right px-3 py-2 font-bold">Amount</th>
                      <th className="w-6"/>
                    </tr></thead>
                    <tbody>
                      {items.map((it, i) => {
                        const amt = parseFloat(it.quantity||0) * parseFloat(it.rate||0);
                        const gst_amt = form.gst_enabled ? amt * parseFloat(it.gst_rate||0) / 100 : 0;
                        return (
                          <tr key={i} className="border-b border-gray-200 hover:bg-blue-50/50">
                            <td className="px-2 py-1">
                              {['sales_invoice','credit_note'].includes(type.key) ? (
                                // Searchable combo — type to filter through ALL
                                // products (finished goods) AND raw materials in
                                // one list. Picking a row dispatches to the
                                // correct setter so the backend gets either
                                // product_id OR material_id (never both).
                                <SalesItemCombo
                                  value={it.product_name || it.material_name || ''}
                                  products={products}
                                  materials={materials}
                                  onPickProduct={(pid) => setProductForItem(i, pid)}
                                  onPickMaterial={(mid) => setMaterialForSalesItem(i, mid)}
                                />
                              ) : (
                                <select value={it.material_id} onChange={e => { const m=materials.find(x=>x.id===e.target.value); setItem(i,'material_id',e.target.value); setItem(i,'material_name',m?.name||''); setItem(i,'unit',m?.unit||'kg'); }} className="w-full bg-transparent text-gray-900 focus:outline-none">
                                  <option value="">Select material...</option>
                                  {materials.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
                                </select>
                              )}
                              {['sales_invoice','credit_note'].includes(type.key) && form.party_id && (it.product_id || it.material_id) && (
                                <div className="mt-0.5">
                                  <AddToPriceListButton
                                    clientId={form.party_id}
                                    product={{
                                      // Whichever picker filled the row decides
                                      // which id we save to the price list.
                                      // iter202 — RM rows now flow through too,
                                      // tagged correctly as item_type:'raw' so
                                      // the next bill auto-fills the rate.
                                      product_id:   it.product_id   || it.material_id,
                                      product_name: it.product_name || it.material_name,
                                      item_type:    it.product_id ? 'finished' : 'raw',
                                      unit:         it.unit,
                                      rate:         it.rate,
                                    }}
                                    currentList={clientPriceList}
                                    onAdded={(entry) => setClientPriceList(prev => [...prev, entry])}
                                  />
                                </div>
                              )}
                              {type.key === 'sales_invoice' && it.product_id && (
                                <BatchPicker item={it} product={products.find(p => p.id === it.product_id)} testId={`batch-picker-${i}`}
                                  onChange={(plan) => setItem(i, 'batches_dispatched', plan)} onSynced={onProductsChanged} />
                              )}
                            </td>
                            <td className="px-1 py-1"><input type="number" min="0" value={it.quantity} onChange={e=>setItem(i,'quantity',e.target.value)} onKeyDown={e=>{ if(e.key==='Enter' && parseFloat(it.quantity)>0 && (it.product_id || it.material_id)){ e.preventDefault(); addItem(); } }} className="w-14 bg-transparent text-gray-900 focus:outline-none text-center font-mono" data-testid={`vqty-${i}`}/></td>
                            <td className="px-1 py-1"><input value={it.unit} onChange={e=>setItem(i,'unit',e.target.value)} className="w-12 bg-transparent text-gray-600 focus:outline-none text-center"/></td>
                            <td className="px-1 py-1"><input type="number" min="0" value={it.rate} onChange={e=>setItem(i,'rate',e.target.value)} className="w-20 bg-transparent text-gray-900 focus:outline-none text-right font-mono"/></td>
                            {form.gst_enabled && (
                              <td className="px-1 py-1">
                                <select value={it.gst_rate} onChange={e=>setItem(i,'gst_rate',e.target.value)} className="w-14 bg-transparent text-gray-600 focus:outline-none">
                                  {[0,5,12,18,28].map(r=><option key={r} value={r}>{r}%</option>)}
                                </select>
                              </td>
                            )}
                            <td className={`px-3 py-1 text-right font-mono font-bold ${tallyTheme.accent}`}>{fmt(amt + (form.gst_enabled ? gst_amt : 0))}</td>
                            <td className="px-1 py-1"><button type="button" onClick={()=>removeItem(i)}><X size={12} className="text-gray-400 hover:text-red-600"/></button></td>
                          </tr>
                        );
                      })}
                      <tr className={tallyTheme.endList}><td colSpan={form.gst_enabled ? 7 : 6} className="text-center text-[10px] py-1 italic">--- End of List ---</td></tr>
                    </tbody>
                  </table>
                </div>
                {/* Tally-style Totals */}
                <div className={`mt-2 ${tallyTheme.tableBg} border border-gray-300 rounded p-3 text-xs space-y-1`}>
                  <div className="flex justify-between font-bold">
                    <span className={tallyTheme.accent}>Entity: {form.gst_enabled ? 'Tirth Industries (GST)' : 'Tirth Enterprises (Non-GST)'}</span>
                  </div>
                  <div className="flex justify-between text-gray-700"><span>Taxable Amount</span><span className="font-mono">{fmt(itemSubtotal)}</span></div>
                  {parseFloat(form.transport_exp || 0) > 0 && (
                    <div className="flex justify-between text-gray-600"><span>Transport Exp</span><span className="font-mono">{fmt(form.transport_exp)}</span></div>
                  )}
                  {otherExtras > 0 && (
                    <div className="flex justify-between text-gray-600"><span>Other charges</span><span className="font-mono">{fmt(otherExtras)}</span></div>
                  )}
                  {form.gst_enabled && <>
                    <div className="flex justify-between text-gray-600"><span>CGST</span><span className="font-mono">{fmt(itemGst/2)}</span></div>
                    <div className="flex justify-between text-gray-600"><span>SGST</span><span className="font-mono">{fmt(itemGst/2)}</span></div>
                  </>}
                  <div className="flex justify-between font-semibold border-t border-gray-300 pt-1"><span>Gross Total</span><span className="font-mono">{fmt(grossTotal)}</span></div>
                  {/* iter211 — Cash / settlement discount input. Visible on all sales-type bills. */}
                  <div className="flex items-center justify-between gap-2 pt-1">
                    <label className="text-gray-700 flex items-center gap-1">
                      <span>Cash Discount</span>
                      <input type="text" placeholder="reason (optional)"
                        value={form.discount_reason || ''} onChange={e => set('discount_reason', e.target.value)}
                        data-testid="voucher-discount-reason"
                        className="ml-1 bg-white border border-gray-300 rounded px-1.5 py-0.5 text-[10px] w-32" />
                    </label>
                    <input type="number" min="0" step="0.01"
                      placeholder="0.00"
                      value={form.discount_amount || ''} onChange={e => set('discount_amount', e.target.value)}
                      data-testid="voucher-discount-amount"
                      className="font-mono text-right bg-white border border-gray-300 rounded px-2 py-0.5 w-24" />
                  </div>
                  {discountAmount > 0 && (
                    <div className="flex justify-between text-emerald-700 text-[11px]"><span>− Discount</span><span className="font-mono">{fmt(discountAmount)}</span></div>
                  )}
                  {Math.abs(roundOff) >= 0.005 && (
                    <div className="flex justify-between text-gray-500 text-[11px]" data-testid="voucher-round-off"><span>Round off</span><span className="font-mono">{roundOff > 0 ? '+' : '−'}{fmt(Math.abs(roundOff))}</span></div>
                  )}
                  <div className={`flex justify-between font-bold border-t-2 border-gray-500 pt-1 ${tallyTheme.accent}`}><span>{type.key === 'credit_note' ? 'Credit to Customer' : 'Net Payable'}</span><span className="font-mono text-base">{fmt(netTotal)}</span></div>
                </div>
                {type.key === 'credit_note' && (
                  <div className="mt-2 space-y-2" data-testid="credit-note-settlement">
                    <label className="flex items-start gap-2 bg-white border border-gray-300 rounded p-2 text-xs cursor-pointer">
                      <input type="checkbox" checked={form.restock !== false} onChange={e => set('restock', e.target.checked)} data-testid="credit-note-restock" className="mt-0.5" />
                      <span>
                        <span className="font-semibold text-gray-800">Goods returned to stock</span>
                        <span className="block text-[10px] text-gray-500">Adds the returned qty back to Finished Goods / Raw Material stock. Untick for a price adjustment or damaged goods that are not restocked.</span>
                      </span>
                    </label>
                    {form.party_id ? (
                      <BillAllocator bills={openBills} alloc={alloc} setAlloc={setAlloc} amount={netTotal} side="debtor" testPrefix="credit-note-alloc" theme="light" />
                    ) : (
                      <div className="text-[10px] text-gray-500 italic">Pick the customer to adjust this credit note against their open invoices (auto FIFO if left blank).</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── MATERIAL INWARD (GRN) ─────────────────────── */}
          {type.key === 'material_inward' && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><label className={labelCls}>Vendor *</label>
                  <select value={form.party_name} onChange={e=>set('party_name',e.target.value)} className={inputCls}>
                    <option value="">Select vendor...</option>
                    {vendors.map(v=><option key={v.id} value={v.name} className="">{v.name}</option>)}
                  </select>
                </div>
                <div><label className={labelCls}>PO Reference</label><input value={form.reference_no||''} onChange={e=>set('reference_no',e.target.value)} className={inputCls} placeholder="PO-260401-001"/></div>
                <div><label className={labelCls}>Vehicle No.</label><input value={form.vehicle_no||''} onChange={e=>set('vehicle_no',e.target.value)} className={inputCls} placeholder="GJ-01-AB-1234"/></div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className={`${tallyTheme.accent} text-sm font-bold`}>Materials Received</label>
                  <button type="button" onClick={addItem} className="text-xs text-blue-700 flex items-center gap-1 hover:text-blue-900"><Plus size={12}/> Add Row</button>
                </div>
                <div className={`${tallyTheme.tableBg} border border-gray-300 rounded overflow-x-auto`}>
                  <table className="w-full text-xs min-w-[520px]">
                    <thead><tr className={`${tallyTheme.tableHead} border-b border-gray-300`}>
                      <th className="text-left px-3 py-2 font-bold">Material</th>
                      <th className="px-2 py-2 font-bold">Lot No.</th>
                      <th className="px-2 py-2 font-bold">Qty Received</th>
                      <th className="px-2 py-2 font-bold">Unit</th>
                      <th className="px-2 py-2 font-bold">Rate (Rs.)</th>
                      <th className="px-2 py-2 font-bold">MFG Date</th>
                      <th className="px-2 py-2 font-bold">Expiry</th>
                      <th className="px-2 py-2 font-bold">Quality</th>
                      <th className="text-right px-3 py-2 font-bold">Value</th>
                      <th/>
                    </tr></thead>
                    <tbody>
                      {items.map((it,i) => (
                        <tr key={i} className="border-b border-gray-200">
                          <td className="px-2 py-1">
                            <select value={it.material_id} onChange={e=>{const m=materials.find(x=>x.id===e.target.value);setItem(i,'material_id',e.target.value);setItem(i,'material_name',m?.name||'');setItem(i,'unit',m?.unit||'kg');}} className="w-full bg-transparent text-gray-900 focus:outline-none">
                              <option value="">Select...</option>
                              {materials.map(m=><option key={m.id} value={m.id} className="">{m.name}</option>)}
                            </select>
                          </td>
                          <td className="px-1 py-1"><input value={it.lot_no} onChange={e=>setItem(i,'lot_no',e.target.value)} placeholder="Auto" className="w-20 bg-transparent text-gray-600 focus:outline-none text-center"/></td>
                          <td className="px-1 py-1"><input type="number" value={it.quantity} onChange={e=>setItem(i,'quantity',e.target.value)} className="w-16 bg-transparent text-gray-900 focus:outline-none text-center"/></td>
                          <td className="px-1 py-1 text-zinc-400">{it.unit||'kg'}</td>
                          <td className="px-1 py-1"><input type="number" value={it.rate} onChange={e=>setItem(i,'rate',e.target.value)} className="w-20 bg-transparent text-gray-900 focus:outline-none text-right"/></td>
                          <td className="px-1 py-1"><input type="date" data-testid={`grn-mfg-${i}`} value={it.mfg_date||''} onChange={e=>setItem(i,'mfg_date',e.target.value)} className="w-28 bg-transparent text-gray-900 focus:outline-none text-xs"/></td>
                          <td className="px-1 py-1"><input type="date" data-testid={`grn-expiry-${i}`} value={it.expiry_date||''} onChange={e=>setItem(i,'expiry_date',e.target.value)} className="w-28 bg-transparent text-gray-900 focus:outline-none text-xs"/></td>
                          <td className="px-1 py-1">
                            <select value={it.quality} onChange={e=>setItem(i,'quality',e.target.value)} className="bg-transparent text-zinc-300 focus:outline-none text-xs">
                              {['Good','Average','Rejected'].map(q=><option key={q} className="">{q}</option>)}
                            </select>
                          </td>
                          <td className="px-3 py-1 text-right font-mono text-blue-800">{fmt((it.quantity||0)*(it.rate||0))}</td>
                          <td className="px-1 py-1"><button type="button" onClick={()=>removeItem(i)}><X size={12} className="text-gray-400 hover:text-red-600"/></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="text-right mt-2 text-amber-400 font-mono text-sm font-semibold">Total: {fmt(itemSubtotal)}</div>
              </div>
            </div>
          )}

          {/* ── MATERIAL OUTWARD (MIV) ─────────────────────── */}
          {type.key === 'material_outward' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Issue To (Batch / Dept.) *</label>
                  <input required value={form.batch_id||''} onChange={e=>set('batch_id',e.target.value)} className={inputCls} placeholder="Batch ID or Department"/>
                </div>
                <div><label className={labelCls}>Authorized By</label>
                  <select value={form.done_by||''} onChange={e=>set('done_by',e.target.value)} className={inputCls}>
                    <option value="">Select...</option>
                    {employees.map(em=><option key={em.id} value={em.name} className="">{em.name} — {em.role}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-gray-700 text-sm font-bold">Materials Issued (FIFO)</label>
                  <button type="button" onClick={addItem} className="text-xs text-blue-700 flex items-center gap-1 hover:text-blue-900"><Plus size={12}/> Add Row</button>
                </div>
                <div className="bg-white border border-gray-300 rounded overflow-x-auto">
                  <table className="w-full text-xs min-w-[520px]">
                    <thead><tr className="border-b border-gray-300 text-gray-700">
                      <th className="text-left px-3 py-2">Material</th>
                      <th className="px-2 py-2">Lot No. (FIFO)</th>
                      <th className="px-2 py-2">Qty</th>
                      <th className="px-2 py-2">Unit</th>
                      <th/>
                    </tr></thead>
                    <tbody>
                      {items.map((it,i) => (
                        <tr key={i} className="border-b border-gray-200">
                          <td className="px-2 py-1">
                            <select value={it.material_id} onChange={e=>{const m=materials.find(x=>x.id===e.target.value);setItem(i,'material_id',e.target.value);setItem(i,'material_name',m?.name||'');setItem(i,'unit',m?.unit||'kg');}} className="w-full bg-transparent text-gray-900 focus:outline-none">
                              <option value="">Select...</option>
                              {materials.map(m=><option key={m.id} value={m.id} className="">{m.name}</option>)}
                            </select>
                          </td>
                          <td className="px-1 py-1"><input value={it.lot_no} onChange={e=>setItem(i,'lot_no',e.target.value)} placeholder="Auto FIFO" className="w-24 bg-transparent text-amber-400 focus:outline-none text-center"/></td>
                          <td className="px-1 py-1"><input type="number" value={it.quantity} onChange={e=>setItem(i,'quantity',e.target.value)} className="w-16 bg-transparent text-gray-900 focus:outline-none text-center"/></td>
                          <td className="px-2 py-1 text-gray-600">{it.unit||'kg'}</td>
                          <td className="px-1 py-1"><button type="button" onClick={()=>removeItem(i)}><X size={12} className="text-gray-400 hover:text-red-600"/></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ── FG INWARD / FG OUTWARD ─────────────────────── */}
          {['fg_inward','fg_outward','sample_issue'].includes(type.key) && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                {type.key === 'fg_outward' && <div><label className={labelCls}>Client / Sales Order</label><input value={form.party_name} onChange={e=>set('party_name',e.target.value)} className={inputCls}/></div>}
                {type.key === 'fg_inward' && <div><label className={labelCls}>Batch Reference</label><input value={form.batch_id||''} onChange={e=>set('batch_id',e.target.value)} className={inputCls}/></div>}
                {type.key === 'sample_issue' && <div><label className={labelCls}>Client / Event / Purpose</label><input value={form.party_name} onChange={e=>set('party_name',e.target.value)} className={inputCls}/></div>}
                {type.key === 'fg_outward' && <div><label className={labelCls}>Vehicle No.</label><input value={form.vehicle_no||''} onChange={e=>set('vehicle_no',e.target.value)} className={inputCls} placeholder="GJ-01-AB-1234"/></div>}
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-gray-700 text-sm font-bold">Products</label>
                  <button type="button" onClick={addItem} className="text-xs text-blue-700 flex items-center gap-1 hover:text-blue-900"><Plus size={12}/> Add Row</button>
                </div>
                <div className="bg-white border border-gray-300 rounded overflow-x-auto">
                  <table className="w-full text-xs min-w-[520px]">
                    <thead><tr className="border-b border-gray-300 text-gray-700">
                      <th className="text-left px-3 py-2">Product</th><th className="px-2 py-2">Qty</th><th className="px-2 py-2">Unit</th><th/>
                    </tr></thead>
                    <tbody>
                      {items.map((it,i) => (
                        <tr key={i} className="border-b border-gray-200">
                          <td className="px-2 py-1">
                            <select value={it.product_id} onChange={e=>{const p=products.find(x=>x.id===e.target.value);setItem(i,'product_id',e.target.value);setItem(i,'product_name',p?.name||'');setItem(i,'unit',p?.unit||'kg');}} className="w-full bg-transparent text-gray-900 focus:outline-none">
                              <option value="">Select...</option>
                              {products.map(p=><option key={p.id} value={p.id} className="">{p.name}</option>)}
                            </select>
                          </td>
                          <td className="px-1 py-1"><input type="number" value={it.quantity} onChange={e=>setItem(i,'quantity',e.target.value)} className="w-16 bg-transparent text-gray-900 focus:outline-none text-center"/></td>
                          <td className="px-2 py-1 text-gray-600">{it.unit||'kg'}</td>
                          <td className="px-1 py-1"><button type="button" onClick={()=>removeItem(i)}><X size={12} className="text-gray-400 hover:text-red-600"/></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ── QC RECORD ─────────────────────── */}
          {type.key === 'qc_record' && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><label className={labelCls}>Batch ID *</label>
                  <select required value={form.batch_id||''} onChange={e=>set('batch_id',e.target.value)} className={inputCls}>
                    <option value="">Select batch...</option>
                    {batches.map(b=><option key={b.id} value={b.id} className="">{b.id} — {b.product_name}</option>)}
                  </select>
                </div>
                <div><label className={labelCls}>Sample Qty</label><input type="number" value={form.sample_qty||''} onChange={e=>set('sample_qty',e.target.value)} className={inputCls}/></div>
                <div><label className={labelCls}>Checked By</label>
                  <select value={form.qc_checked_by||''} onChange={e=>set('qc_checked_by',e.target.value)} className={inputCls}>
                    <option value="">Select...</option>
                    {employees.map(em=><option key={em.id} value={em.name} className="">{em.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-gray-700 text-sm font-bold mb-2 block">QC Parameters</label>
                <div className="bg-white border border-gray-300 rounded overflow-x-auto">
                  <table className="w-full text-xs min-w-[520px]">
                    <thead><tr className="border-b border-gray-300 text-gray-700">
                      <th className="text-left px-3 py-2">Parameter</th><th className="px-3 py-2">Standard</th><th className="px-3 py-2">Actual Value</th><th className="px-3 py-2">Result</th>
                    </tr></thead>
                    <tbody>
                      {qcParams.map((p,i) => (
                        <tr key={i} className="border-b border-gray-200">
                          <td className="px-3 py-2 text-zinc-300">{p.name}</td>
                          <td className="px-3 py-2 text-zinc-500">{p.standard}</td>
                          <td className="px-2 py-1"><input value={p.actual} onChange={e=>{const n=[...qcParams];n[i]={...n[i],actual:e.target.value};setQcParams(n);}} className="w-full bg-transparent text-gray-900 focus:outline-none"/></td>
                          <td className="px-2 py-1">
                            <select value={p.result} onChange={e=>{const n=[...qcParams];n[i]={...n[i],result:e.target.value};setQcParams(n);}} className={`bg-transparent text-xs focus:outline-none ${p.result==='Pass'?'text-green-400':'text-red-400'}`}>
                              <option value="Pass" className="bg-zinc-900 text-green-400">Pass</option>
                              <option value="Fail" className="bg-zinc-900 text-red-400">Fail</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className={`mt-2 text-center py-2 rounded-xl text-sm font-bold ${qcParams.every(p=>p.result==='Pass')?'bg-green-900/40 text-green-300':'bg-red-900/40 text-red-300'}`}>
                  Overall Result: {qcParams.every(p=>p.result==='Pass') ? '✓ PASS' : '✗ FAIL'}
                </div>
              </div>
            </div>
          )}

          {/* ── WASTAGE ─────────────────────── */}
          {type.key === 'wastage' && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><label className={labelCls}>Batch Reference</label><input value={form.batch_id||''} onChange={e=>set('batch_id',e.target.value)} className={inputCls} placeholder="Batch ID"/></div>
                <div><label className={labelCls}>Wastage Type</label>
                  <select value={form.wastage_type||'Process'} onChange={e=>set('wastage_type',e.target.value)} className={inputCls}>
                    {['Process Loss','Packaging Damage','Spillage','Sampling','Moisture Loss','Rejection','Other'].map(w=><option key={w} className="">{w}</option>)}
                  </select>
                </div>
                <div><label className={labelCls}>Loss %</label><input type="number" step="any" value={form.loss_percentage||''} onChange={e=>set('loss_percentage',e.target.value)} className={inputCls}/></div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-gray-700 text-sm font-bold">Wastage Items</label>
                  <button type="button" onClick={addItem} className="text-xs text-blue-700 flex items-center gap-1 hover:text-blue-900"><Plus size={12}/> Add Row</button>
                </div>
                <div className="bg-white border border-gray-300 rounded overflow-x-auto">
                  <table className="w-full text-xs min-w-[520px]">
                    <thead><tr className="border-b border-gray-300 text-gray-700">
                      <th className="text-left px-3 py-2">Item Type</th><th className="px-3 py-2">Item</th><th className="px-2 py-2">Qty</th><th className="px-2 py-2">Unit</th><th/>
                    </tr></thead>
                    <tbody>
                      {items.map((it,i) => (
                        <tr key={i} className="border-b border-gray-200">
                          <td className="px-2 py-1">
                            <select value={it.item_type||'raw'} onChange={e=>setItem(i,'item_type',e.target.value)} className="bg-transparent text-gray-600 focus:outline-none text-xs">
                              <option value="raw" className="bg-zinc-900">Raw Material</option>
                              <option value="finished" className="bg-zinc-900">Finished Good</option>
                            </select>
                          </td>
                          <td className="px-2 py-1">
                            {(it.item_type||'raw')==='raw' ? (
                              <select value={it.material_id} onChange={e=>{const m=materials.find(x=>x.id===e.target.value);setItem(i,'material_id',e.target.value);setItem(i,'material_name',m?.name||'');setItem(i,'unit',m?.unit||'kg');}} className="w-full bg-transparent text-gray-900 focus:outline-none">
                                <option value="">Select...</option>{materials.map(m=><option key={m.id} value={m.id} className="">{m.name}</option>)}
                              </select>
                            ) : (
                              <select value={it.product_id} onChange={e=>{const p=products.find(x=>x.id===e.target.value);setItem(i,'product_id',e.target.value);setItem(i,'product_name',p?.name||'');setItem(i,'unit',p?.unit||'kg');}} className="w-full bg-transparent text-gray-900 focus:outline-none">
                                <option value="">Select...</option>{products.map(p=><option key={p.id} value={p.id} className="">{p.name}</option>)}
                              </select>
                            )}
                          </td>
                          <td className="px-1 py-1"><input type="number" value={it.quantity} onChange={e=>setItem(i,'quantity',e.target.value)} className="w-16 bg-transparent text-gray-900 focus:outline-none text-center"/></td>
                          <td className="px-2 py-1 text-gray-600">{it.unit||'kg'}</td>
                          <td className="px-1 py-1"><button type="button" onClick={()=>removeItem(i)}><X size={12} className="text-gray-400 hover:text-red-600"/></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ── MAINTENANCE ─────────────────────── */}
          {type.key === 'maintenance' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Machine *</label>
                  <select required value={form.machine_id||''} onChange={e=>{const m=machines.find(x=>x.id===e.target.value);set('machine_id',e.target.value);set('machine_name',m?.name||'');}} className={inputCls}>
                    <option value="">Select machine...</option>
                    {machines.map(m=><option key={m.id} value={m.id} className="">{m.name} — {m.type}</option>)}
                  </select>
                </div>
                <div><label className={labelCls}>Maintenance Type</label>
                  <select value={form.maintenance_type||'Preventive'} onChange={e=>set('maintenance_type',e.target.value)} className={inputCls}>
                    {['Preventive','Breakdown','Calibration','Cleaning','Lubrication','Annual','Upgrade'].map(t=><option key={t} className="">{t}</option>)}
                  </select>
                </div>
              </div>
              <div><label className={labelCls}>Work Done / Description *</label>
                <textarea required rows={2} value={form.narration} onChange={e=>set('narration',e.target.value)} className={`${inputCls} resize-none`} placeholder="Describe the maintenance work performed..."/>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><label className={labelCls}>Cost (₹)</label><input type="number" value={form.maintenance_cost||''} onChange={e=>set('maintenance_cost',e.target.value)} className={inputCls}/></div>
                <div><label className={labelCls}>Done By</label>
                  <select value={form.done_by||''} onChange={e=>set('done_by',e.target.value)} className={inputCls}>
                    <option value="">Select...</option>
                    {employees.map(em=><option key={em.id} value={em.name} className="">{em.name}</option>)}
                  </select>
                </div>
                <div><label className={labelCls}>Next Maintenance Date</label><input type="date" value={form.next_maintenance_date||''} onChange={e=>set('next_maintenance_date',e.target.value)} className={inputCls}/></div>
              </div>
            </div>
          )}

          {/* ── PRODUCTION ORDER / REWORK ─────────────────────── */}
          {['production_order','rework','rejection','stock_transfer'].includes(type.key) && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Reference (Batch/Order)</label><input value={form.batch_id||''} onChange={e=>set('batch_id',e.target.value)} className={inputCls}/></div>
                <div><label className={labelCls}>Authorized By</label>
                  <select value={form.done_by||''} onChange={e=>set('done_by',e.target.value)} className={inputCls}>
                    <option value="">Select...</option>
                    {employees.map(em=><option key={em.id} value={em.name} className="">{em.name}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Narration (Tally-style yellow bar) */}
          {!['journal'].includes(type.key) && (
            <div className={`${tallyTheme.narr} rounded border border-gray-300 p-2`}>
              <label className="text-gray-700 text-[10px] uppercase tracking-wider font-bold block mb-1">Narration</label>
              <textarea rows={2} value={form.narration} onChange={e=>set('narration',e.target.value)} className="w-full bg-transparent text-gray-900 text-sm focus:outline-none resize-none" placeholder="Brief description of this transaction..."/>
            </div>
          )}

          {/* Submit - Tally style buttons */}
          <div className="flex gap-2 pt-2 border-t border-gray-400">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded border border-gray-400 text-gray-700 hover:bg-gray-200 text-sm font-medium transition-colors">Esc: Cancel</button>
            <button type="submit" disabled={submitting} data-testid="voucher-submit" className="flex-grow-[2] flex-1 py-2 rounded bg-[#1a237e] hover:bg-[#283593] text-white font-bold text-sm disabled:opacity-50 transition-colors">
              {submitting ? 'Saving...' : `Ctrl+A: Accept ${type.label}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Voucher Detail View ───────────────────────────────────────────────────
function VoucherDetail({ voucher, onClose }) {
  const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#0F0F11] border border-zinc-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800">
          <div>
            <div className="font-mono text-amber-400 font-bold text-lg">{voucher.voucher_number}</div>
            <div className="text-zinc-400 text-xs mt-0.5">{voucher.voucher_label} · {voucher.date}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => printPdf(`${API}/vouchers/${voucher.id}/pdf`)} className="text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-300 px-3 py-1.5 rounded-lg flex items-center gap-1"><Printer size={12}/> Print</button>
            <button onClick={onClose}><X size={18} className="text-zinc-500 hover:text-zinc-200"/></button>
          </div>
        </div>
        <div className="p-6 space-y-4" id="voucher-print">
          <div className="grid grid-cols-2 gap-4 text-sm">
            {voucher.party_name && <div><span className="text-zinc-500">Party: </span><span className="text-zinc-100 font-medium">{voucher.party_name}</span></div>}
            {voucher.batch_id && <div><span className="text-zinc-500">Batch: </span><span className="font-mono text-amber-400">{voucher.batch_id}</span></div>}
            {voucher.reference_no && <div><span className="text-zinc-500">Reference: </span><span className="text-zinc-300">{voucher.reference_no}</span></div>}
            {voucher.payment_mode && <div><span className="text-zinc-500">Mode: </span><span className="text-zinc-300">{voucher.payment_mode}</span></div>}
            {voucher.customer_payment && <div><span className="text-zinc-500">Payment made to debtor: </span><span className="text-amber-300">{voucher.payment_purpose || 'Refund'} — debited to their ledger</span></div>}
            <div><span className="text-zinc-500">Created by: </span><span className="text-zinc-300">{voucher.created_by}</span></div>
            <div><span className="text-zinc-500">Status: </span><span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[voucher.status]||'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>{voucher.status}</span></div>
          </div>

          {voucher.items?.length > 0 && (
            <div className="bg-white border border-gray-300 rounded overflow-x-auto">
              <table className="w-full text-xs min-w-[520px]">
                <thead><tr className="border-b border-gray-300 text-gray-700">
                  <th className="text-left px-3 py-2">Item</th><th className="px-2 py-2 text-right">Qty</th><th className="px-2 py-2">Unit</th><th className="px-2 py-2 text-right">Rate</th><th className="px-3 py-2 text-right">Amount</th>
                </tr></thead>
                <tbody>
                  {voucher.items.map((it,i) => (
                    <tr key={i} className="border-b border-gray-200">
                      <td className="px-3 py-2 text-gray-900 font-medium">{it.material_name||it.product_name||it.item_name||'—'}<BatchChips batches={it.batches_dispatched} light /></td>
                      <td className="px-2 py-2 text-right font-mono text-gray-800">{it.quantity}</td>
                      <td className="px-2 py-2 text-gray-600">{it.unit}</td>
                      <td className="px-2 py-2 text-right font-mono text-gray-700">{it.rate ? fmt(it.rate) : '—'}</td>
                      <td className="px-3 py-2 text-right font-mono text-blue-800">{it.rate ? fmt((it.quantity||0)*(it.rate||0)) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {voucher.qc_parameters?.length > 0 && (
            <div className="bg-white border border-gray-300 rounded overflow-x-auto">
              <table className="w-full text-xs min-w-[520px]">
                <thead><tr className="border-b border-gray-300 text-gray-700"><th className="text-left px-3 py-2">Parameter</th><th className="px-3 py-2">Standard</th><th className="px-3 py-2">Actual</th><th className="px-3 py-2">Result</th></tr></thead>
                <tbody>{voucher.qc_parameters.map((p,i)=>(
                  <tr key={i} className="border-b border-gray-200">
                    <td className="px-3 py-2 text-gray-900">{p.name}</td>
                    <td className="px-3 py-2 text-gray-600">{p.standard}</td>
                    <td className="px-3 py-2 text-gray-800">{p.actual||'—'}</td>
                    <td className="px-3 py-2"><span className={p.result==='Pass'?'text-green-700 font-semibold':'text-red-700 font-semibold'}>{p.result}</span></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}

          {voucher.amount > 0 && (
            <div className="flex justify-end">
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-6 py-3">
                <div className="text-zinc-400 text-xs">Total Amount</div>
                <div className="font-mono font-bold text-amber-400 text-2xl">{fmt(voucher.amount || voucher.maintenance_cost)}</div>
              </div>
            </div>
          )}

          {voucher.narration && <div className="bg-zinc-800/40 rounded-xl p-4 text-zinc-400 text-sm italic">"{voucher.narration}"</div>}
        </div>
      </div>
    </div>
  );
}

// ─── Main Vouchers Page ────────────────────────────────────────────────────
export default function Vouchers() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const [view, setView] = useState('daybook'); // daybook | type key
  const [vouchers, setVouchers] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [editingVoucher, setEditingVoucher] = useState(null);
  const [showDetail, setShowDetail] = useState(null);
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [previewOrderId, setPreviewOrderId] = useState(null);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState(fyStart());
  const [dateTo, setDateTo] = useState(today());
  // Sortable columns — default is Invoice/Voucher No. DESC so the newest
  // bill always sits on top per the user's spec (Feb-2026).
  // Clicking a header toggles asc/desc; clicking a different header
  // switches the key and resets to desc.
  const [sortKey, setSortKey] = useState('voucher_number');
  const [sortDir, setSortDir] = useState('desc');
  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('desc'); }
  };

  // Master data
  const [vendors, setVendors] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [products, setProducts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [machines, setMachines] = useState([]);
  const [clients, setClients] = useState([]);
  // Unified bill list — Sales Invoice tab now reads from /invoices/bill-log
  // (the SAME source as /bill-log and Enterprise Bill History) so all three
  // surfaces stay in lock-step. Numbering is the canonical TI/GST/... series
  // instead of the legacy INV-260520-006 parallel system.
  const [unifiedBills, setUnifiedBills] = useState([]);
  // Legacy sub-tab inside Sales Invoice — surfaces old sales_invoice
  // vouchers that have no `linked_order_id` (created before the unified
  // pipeline landed). Read-only, kept for audit only.
  const [salesInvoiceSubTab, setSalesInvoiceSubTab] = useState('current'); // 'current' | 'legacy'

  // Define fetchAll before useAdminDelete to avoid TDZ error
  const fetchAll = useCallback(async () => {
    try {
      const [vRes, sumRes, vendRes, matRes, prodRes, batRes, empRes, machRes, cliRes, billsRes] = await Promise.all([
        axios.get(`${API}/vouchers/?date_from=${dateFrom}&date_to=${dateTo}${search ? `&search=${search}` : ''}`, { withCredentials: true }),
        axios.get(`${API}/vouchers/summary`, { withCredentials: true }),
        axios.get(`${API}/purchases/vendors`, { withCredentials: true }),
        axios.get(`${API}/inventory/raw-materials`, { withCredentials: true }),
        axios.get(`${API}/inventory/finished-goods`, { withCredentials: true }),
        axios.get(`${API}/production/batches`, { withCredentials: true }),
        axios.get(`${API}/workforce/employees`, { withCredentials: true }),
        axios.get(`${API}/vouchers/machines`, { withCredentials: true }),
        axios.get(`${API}/clients/`, { withCredentials: true }),
        axios.get(`${API}/invoices/bill-log?invoice_type=GST&date_from=${dateFrom}&date_to=${dateTo}`, { withCredentials: true }),
      ]);
      setVouchers(vRes.data); setSummary(sumRes.data);
      setUnifiedBills(billsRes.data);
      setVendors(vendRes.data); setMaterials(matRes.data); setProducts(prodRes.data);
      setBatches(batRes.data); setEmployees(empRes.data); setMachines(machRes.data);
      setClients(cliRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, search]);

  const { deleteState, requestDelete, confirmDelete, cancelDelete } = useAdminDelete(fetchAll);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreate = (type) => { setSelectedType(type); setEditingVoucher(null); setShowCreate(true); };
  const handleSubmit = async (payload) => {
    if (editingVoucher) {
      // Edit-mode → PUT to /vouchers/{id}; backend recomputes amount + syncs
      // the linked sales_orders row so Bill List / PDF stay consistent.
      await axios.put(`${API}/vouchers/${editingVoucher.id}`, payload, { withCredentials: true });
      toast.success('Voucher updated');
    } else {
      await axios.post(`${API}/vouchers/`, payload, { withCredentials: true });
    }
    setShowCreate(false); setEditingVoucher(null); fetchAll();
  };
  const handleEditVoucher = (v) => {
    const type = ALL_TYPES.find(t => t.key === v.voucher_type);
    if (!type) { toast.error('Unknown voucher type'); return; }
    setSelectedType(type);
    setEditingVoucher(v);
    setShowCreate(true);
  };
  const [cancelModal, setCancelModal] = useState({ open: false, id: null });
  const handleCancel = (id) => {
    setCancelModal({ open: true, id });
  };
  const confirmCancelVoucher = async () => {
    const { id } = cancelModal;
    setCancelModal({ open: false, id: null });
    try {
      await axios.patch(`${API}/vouchers/${id}/cancel`, { reason: 'User cancelled' }, { withCredentials: true });
      fetchAll();
      toast.success('Voucher cancelled');
    } catch (e) { toast.error(e.response?.data?.detail || 'Cancel failed'); }
  };

  // Sales Invoice tab should ONLY show GST-enabled vouchers — non-GST bills
  // belong to the Enterprise (Tirth Enterprises) ledger and are managed
  // separately on /enterprise. Other tabs are unfiltered.
  //
  // Sales Invoice tab gets an extra client-side filter on voucher_number —
  // typing "065" surfaces the bill TI/GST/26-27/065 instantly (the search
  // input also still hits the backend by `search` query for party_name).
  // Sort respects `sortKey` + `sortDir` (see header click handlers).
  const searchTokens = (search || '').trim().toLowerCase();
  // Numeric-aware compare on the trailing digits — keeps
  // "TI/GST/26-27/100" > "TI/GST/26-27/099"; falls back to plain string
  // compare when the input has no trailing digits.
  const seq = (s) => parseInt(((s || '').match(/(\d+)$/) || ['0', '0'])[1], 10);
  const cmp = (a, b) => {
    let va, vb;
    switch (sortKey) {
      case 'voucher_number': va = seq(a.voucher_number); vb = seq(b.voucher_number); break;
      case 'amount':         va = parseFloat(a.amount || a.maintenance_cost || 0); vb = parseFloat(b.amount || b.maintenance_cost || 0); break;
      case 'party':          va = (a.party_name || a.machine_name || a.batch_id || '').toLowerCase(); vb = (b.party_name || b.machine_name || b.batch_id || '').toLowerCase(); break;
      case 'type':           va = (a.voucher_type || '').toLowerCase(); vb = (b.voucher_type || '').toLowerCase(); break;
      case 'reference':      va = (a.reference_no || '').toLowerCase(); vb = (b.reference_no || '').toLowerCase(); break;
      case 'status':         va = (a.status || '').toLowerCase(); vb = (b.status || '').toLowerCase(); break;
      case 'payment_status': {
        // For the Sales Invoice tab — chips sort Paid > Partial > Unpaid
        const rank = { 'Paid': 3, 'Partial': 2, 'Partially Paid': 2, 'Unpaid': 1 };
        va = rank[a.payment_status || 'Unpaid'] || 0;
        vb = rank[b.payment_status || 'Unpaid'] || 0;
        break;
      }
      case 'date':
      default:               va = a.date || ''; vb = b.date || ''; break;
    }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ?  1 : -1;
    return 0;
  };
  // Sales Invoice tab — RESTORED to the original voucher-row UI (Feb-2026
  // user feedback: "get back to the old popup model showing the same sales
  // invoice UI"). Filter is back to the simple "gst_enabled=true + matches
  // voucher_number search". No more Current/Legacy sub-tabs.
  const filtered = vouchers
    .filter(v => {
      if (view === 'daybook') return true;
      if (v.voucher_type !== view) return false;
      if (view === 'sales_invoice') {
        if (v.gst_enabled !== true) return false;
        if (searchTokens) {
          const vn = (v.voucher_number || '').toLowerCase();
          const tail = vn.match(/(\d+)$/)?.[1] || '';
          if (!vn.includes(searchTokens) && !tail.includes(searchTokens)) return false;
        }
        return true;
      }
      return true;
    })
    .sort(cmp);
  const currentTypeInfo = view !== 'daybook' ? ALL_TYPES.find(t => t.key === view) : null;

  if (loading) return <div className="text-zinc-400 animate-pulse p-8">Loading vouchers...</div>;

  return (
    <div className="flex flex-col md:flex-row gap-0 h-[calc(100vh-7rem)] -m-4 md:-m-6" data-testid="vouchers-page">
      {/* ── LEFT SIDEBAR (desktop) ── */}
      <div className="hidden md:block w-56 flex-shrink-0 bg-[#0A0A0C] border-r border-zinc-800 overflow-y-auto">
        {/* Day Book */}
        <button onClick={() => setView('daybook')} className={`w-full text-left px-4 py-3 flex items-center gap-2 text-sm font-semibold border-b border-zinc-800 transition-colors ${view === 'daybook' ? 'bg-amber-500/10 text-amber-400' : 'text-zinc-300 hover:bg-zinc-800'}`}>
          <BookOpen size={15}/> Day Book
          <span className="ml-auto text-xs bg-zinc-700 text-zinc-300 px-1.5 rounded-full">{summary.this_month || 0}</span>
        </button>

        {CATEGORIES.map(cat => {
          const Icon = cat.icon;
          return (
            <div key={cat.id} className="border-b border-zinc-800/60">
              <div className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-wider ${cat.color}`}>
                <Icon size={12}/>{cat.name}
              </div>
              {cat.types.map(type => {
                const count = summary.by_type?.[type.key] || 0;
                const active = view === type.key;
                return (
                  <button key={type.key} onClick={() => setView(type.key)} data-testid={`voucher-type-${type.key}`}
                    className={`w-full text-left px-4 py-2 text-xs flex items-center justify-between transition-all ${active ? `${cat.bg} ${cat.color} font-semibold border-r-2 border-amber-500` : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-200'}`}>
                    <span>{type.label}</span>
                    <div className="flex items-center gap-1.5">
                      {type.hint && <span className="text-zinc-600 font-mono text-xs hidden lg:inline">{type.hint}</span>}
                      {count > 0 && <span className="bg-zinc-700 text-zinc-300 px-1 rounded text-xs">{count}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* ── MAIN CONTENT ── */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Mobile voucher-type picker (sidebar is hidden below md) */}
        <div className="md:hidden px-3 py-2 border-b border-zinc-800 bg-[#0A0A0C]">
          <select value={view} onChange={e => setView(e.target.value)} data-testid="voucher-view-select-mobile"
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-2 text-zinc-200 text-xs focus:outline-none">
            <option value="daybook">Day Book — All Transactions ({summary.this_month || 0})</option>
            {CATEGORIES.map(cat => (
              <optgroup key={cat.id} label={cat.name}>
                {cat.types.map(type => (
                  <option key={type.key} value={type.key}>{type.label}{summary.by_type?.[type.key] ? ` (${summary.by_type[type.key]})` : ''}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 px-3 sm:px-5 py-3 border-b border-zinc-800 bg-[#0A0A0C] flex-shrink-0">
          <div>
            <h2 className="font-heading font-bold text-zinc-50 text-base">
              {view === 'daybook' ? 'Day Book — All Transactions' : currentTypeInfo?.label}
            </h2>
            <p className="text-zinc-500 text-xs">{view === 'daybook' ? `${filtered.length} entries` : currentTypeInfo?.desc}</p>
          </div>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-zinc-300 text-xs focus:outline-none" />
            <span className="text-zinc-600 text-xs">to</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-zinc-300 text-xs focus:outline-none" />
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-2 text-zinc-500"/>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="bg-zinc-800 border border-zinc-700 rounded-lg pl-8 pr-3 py-1.5 text-zinc-300 text-xs focus:outline-none w-36" />
            </div>
            <IntegrityBadge isAdmin={isAdmin} refreshKey={vouchers.length} />
            <button onClick={() => {
              const type = view === 'daybook' ? ALL_TYPES[0] : ALL_TYPES.find(t => t.key === view);
              if (type) handleCreate(type);
              else handleCreate(ALL_TYPES[0]);
            }} className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs px-3 py-2 rounded-xl" data-testid="new-voucher-btn">
              <Plus size={14}/> New {view !== 'daybook' && currentTypeInfo ? currentTypeInfo.label : 'Voucher'}
            </button>
          </div>
        </div>

        {/* Summary cards (day book mode) */}
        {view === 'daybook' && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-5 py-3 border-b border-zinc-800 bg-[#0A0A0C] flex-shrink-0">
            {[
              { label: 'Total Vouchers', value: summary.total || 0, color: 'text-zinc-50' },
              { label: 'This Month', value: summary.this_month || 0, color: 'text-blue-400' },
              { label: 'Today', value: summary.today || 0, color: 'text-amber-400' },
              { label: 'Pending Approval', value: summary.pending_approval || 0, color: 'text-yellow-400' },
            ].map(s => (
              <div key={s.label} className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3">
                <div className="text-zinc-500 text-xs">{s.label}</div>
                <div className={`font-heading font-bold text-xl ${s.color}`}>{s.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Voucher table */}
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="sticky top-0 bg-[#0A0A0C] z-10">
              <tr className="border-b border-zinc-800">
                {(view === 'sales_invoice' ? [
                  { label: 'Date',                key: 'date' },
                  { label: 'Invoice No.',         key: 'voucher_number' },
                  { label: 'Type',                key: 'type' },
                  { label: 'Particulars / Party', key: 'party' },
                  { label: 'Ref',                 key: 'reference' },
                  { label: 'Amount',              key: 'amount' },
                  { label: 'Payment Status',      key: 'payment_status' },
                  { label: 'Actions',             key: null },
                ] : [
                  { label: 'Date',                key: 'date' },
                  { label: 'Voucher No.',         key: 'voucher_number' },
                  { label: 'Type',                key: 'type' },
                  { label: 'Particulars / Party', key: 'party' },
                  { label: 'Ref',                 key: 'reference' },
                  { label: 'Amount',              key: 'amount' },
                  { label: 'Status',              key: 'status' },
                  { label: 'Actions',             key: null },
                ]).map(({ label, key }) => {
                  const isActive = key && key === sortKey;
                  return (
                    <th key={label}
                      onClick={key ? () => toggleSort(key) : undefined}
                      className={`text-left text-xs font-medium px-4 py-3 whitespace-nowrap select-none ${key ? 'cursor-pointer hover:text-zinc-200' : ''} ${isActive ? 'text-amber-400' : 'text-zinc-500'}`}
                      data-testid={key ? `vouchers-sort-${key}` : undefined}
                      title={key ? 'Click to sort' : ''}>
                      {label}
                      {isActive && <span className="ml-1 text-[10px]">{sortDir === 'asc' ? '↑' : '↓'}</span>}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filtered.map(v => {
                const typeInfo = ALL_TYPES.find(t => t.key === v.voucher_type);
                const catId = typeInfo?.category || 'accounting';
                const cat = CATEGORIES.find(c => c.id === catId);
                return (
                  <tr key={v.id} className="border-b border-zinc-800/40 hover:bg-zinc-800/20 transition-colors cursor-pointer group" onClick={() => setShowDetail(v)} data-testid={`voucher-row-${v.id}`}>
                    <td className="px-4 py-3 font-mono text-zinc-400 text-xs">{v.date}</td>
                    <td className="px-4 py-3 font-mono text-amber-400 text-xs font-bold">
                      {v.voucher_number}
                      {v.invoice_number && v.invoice_number !== v.voucher_number && <div className="text-[10px] text-zinc-400 font-normal" data-testid={`voucher-invoice-no-${v.id}`}>{v.invoice_number}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${cat?.border || 'border-zinc-700'} ${cat?.bg || 'bg-zinc-800'} ${cat?.color || 'text-zinc-400'}`}>
                        {typeInfo?.label || v.voucher_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-zinc-200 text-xs max-w-48 truncate">
                      {v.party_name || v.machine_name || v.batch_id || v.narration || '—'}
                      {v.customer_payment && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30" data-testid={`customer-payment-tag-${v.id}`}>to debtor · {v.payment_purpose || 'Refund'}</span>}
                    </td>
                    <td className="px-4 py-3 font-mono text-zinc-500 text-xs">{v.reference_no || '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs font-semibold">
                      {v.amount || v.maintenance_cost ? (
                        <span className={v.voucher_type?.includes('receipt') || v.voucher_type === 'sales_invoice' ? 'text-green-400' : 'text-red-400'}>
                          {fmt(v.amount || v.maintenance_cost)}
                        </span>
                      ) : <span className="text-zinc-600">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {view === 'sales_invoice' ? (
                        (() => {
                          const ps = v.payment_status || 'Unpaid';
                          const styleMap = {
                            'Paid':    'bg-green-900/40 text-green-300 border-green-700',
                            'Partial': 'bg-amber-900/40 text-amber-300 border-amber-700',
                            'Partially Paid': 'bg-amber-900/40 text-amber-300 border-amber-700',
                            'Unpaid':  'bg-red-900/30 text-red-400 border-red-800',
                          };
                          const label = ps === 'Partial' ? 'Partially Paid' : ps;
                          return (
                            <span data-testid={`voucher-payment-status-${v.id}`}
                              className={`text-xs px-2 py-0.5 rounded-full border ${styleMap[ps] || 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>
                              {label}
                            </span>
                          );
                        })()
                      ) : (
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[v.status] || 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>
                          {v.status}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        {v.voucher_type === 'sales_invoice' ? (
                          <>
                            {/* View — opens read-only print preview (the
                                same preview operators use before printing). */}
                            <button onClick={() => setPreviewOrderId(v.linked_order_id || `vinv-${String(v.id || '').slice(0, 8)}`)}
                              className="p-1.5 rounded-lg bg-zinc-800 text-blue-400 hover:text-blue-300"
                              title="View bill (read-only)"
                              data-testid={`voucher-view-${v.id}`}>
                              <Eye size={12}/>
                            </button>
                            {/* Send / Share / Attach COA / Attach Challan /
                                Record Payment — all in the existing
                                InvoiceModal. */}
                            <button onClick={async () => {
                              try {
                                if (v.linked_order_id) {
                                  const res = await axios.get(`${API}/sales/orders?include_history=true`, { withCredentials: true });
                                  const order = res.data.find(o => o.id === v.linked_order_id);
                                  if (order) { setInvoiceOrder(order); return; }
                                }
                                // Robust totals — voucher items may use `quantity`/`qty`,
                                // GST may be per-line or voucher-level, and legacy vouchers
                                // may have `amount` unset. Compute everything locally and
                                // only fall back to the stored amount when it's > 0.
                                const items = v.items || [];
                                const v_fallback = parseFloat(v.gst_rate || 0) || 0;
                                const sub = items.reduce(
                                  (s, it) => s + parseFloat(it.quantity || it.qty || 0) * parseFloat(it.rate || 0),
                                  0,
                                );
                                const gst = (v.gst_enabled === false) ? 0 : items.reduce(
                                  (s, it) => s + parseFloat(it.quantity || it.qty || 0) * parseFloat(it.rate || 0)
                                    * (parseFloat(it.gst_rate !== undefined && it.gst_rate !== null ? it.gst_rate : v_fallback) / 100),
                                  0,
                                );
                                const transport = parseFloat(v.transport_exp || 0) || 0;
                                const extras = (v.extra_charges || []).reduce((s, c) => s + parseFloat(c.amount || 0), 0);
                                const computed = sub + gst + transport + extras;
                                const stored = parseFloat(v.amount || v.total_amount || 0) || 0;
                                const total = stored > 0 ? stored : computed;
                                const paid = parseFloat(v.paid_amount || 0) || 0;
                                const orderLike = {
                                  id: v.linked_order_id || `vinv-${String(v.id || '').slice(0, 8)}`,
                                  invoice_number: v.invoice_number || v.voucher_number,
                                  client: v.party_name, client_address: v.client_address || '',
                                  client_phone: v.client_phone || '', client_gstin: v.client_gstin || '',
                                  client_state: v.client_state || '',
                                  gst_enabled: v.gst_enabled, gst_rate: v.gst_rate || 0,
                                  items,
                                  total_amount: Math.round(total * 100) / 100,
                                  subtotal: Math.round(sub * 100) / 100,
                                  gst_amount: Math.round(gst * 100) / 100,
                                  transport_exp: transport,
                                  payment_status: v.payment_status || (paid >= total && total > 0 ? 'Paid' : paid > 0 ? 'Partial' : 'Unpaid'),
                                  paid_amount: paid,
                                  payments: v.payments || [],
                                  dispatch_through: v.dispatch_through || '',
                                  destination: v.destination || '',
                                  payment_terms: v.payment_terms || '',
                                  created_at: v.created_at, invoice_date: v.date,
                                  source: 'voucher',
                                };
                                setInvoiceOrder(orderLike);
                              } catch { toast.error('Error loading invoice'); }
                            }}
                              className="p-1.5 rounded-lg bg-green-900/30 text-green-300 hover:text-green-200 border border-green-700/40"
                              title="Send / Attach COA / Attach Challan / Record Payment"
                              data-testid={`voucher-share-${v.id}`}>
                              <FileText size={12}/>
                            </button>
                            {/* Edit — pre-fills VoucherForm + PUT */}
                            {v.status !== 'cancelled' && (
                              <button onClick={() => handleEditVoucher(v)}
                                className="p-1.5 rounded-lg bg-zinc-800 text-blue-400 hover:text-blue-300"
                                title="Edit voucher items / rates / party"
                                data-testid={`voucher-edit-${v.id}`}>
                                <Edit2 size={12}/>
                              </button>
                            )}
                          </>
                        ) : (
                          <>
                            <button onClick={() => setShowDetail(v)} className="p-1.5 rounded-lg bg-zinc-800 text-zinc-400 hover:text-zinc-200" title="View"><Eye size={12}/></button>
                            {/* Edit for non-sales vouchers (purchase invoices sync back to the bill, ledger and lots) */}
                            {v.status !== 'cancelled' && (
                              <button onClick={() => handleEditVoucher(v)}
                                className="p-1.5 rounded-lg bg-zinc-800 text-blue-400 hover:text-blue-300"
                                title={v.voucher_type === 'purchase_invoice' ? 'Edit — updates the purchase bill, creditor ledger and lots too' : 'Edit voucher'}
                                data-testid={`voucher-edit-${v.id}`}>
                                <Edit2 size={12}/>
                              </button>
                            )}
                          </>
                        )}
                        {/* Quick Record Payment — only for purchase_invoice
                            vouchers that carry a `linked_bill_id` (auto-set
                            when the voucher was created from a purchase
                            bill scan).  Calls the existing PUT
                            /api/purchases/bills/{id}/payment endpoint that
                            handles partial / paid / overpayment and
                            mirrors the change into cashflow. */}
                        {v.voucher_type === 'purchase_invoice' && v.linked_bill_id && v.status !== 'cancelled' && (
                          <button onClick={async () => {
                            const outstanding = parseFloat(v.amount || 0);
                            const amt = window.prompt(`Record payment for ${v.voucher_number || v.party_name}.\nOutstanding: ₹${outstanding}. Enter amount to mark as paid (use the full amount for "Paid", less for "Partial"):`, outstanding.toFixed(0));
                            if (!amt || isNaN(parseFloat(amt))) return;
                            const method = window.prompt('Payment method (Cash / Bank / UPI / Cheque):', 'Bank') || 'Bank';
                            try {
                              await axios.put(`${API}/purchases/bills/${v.linked_bill_id}/payment`, {
                                paid_amount: parseFloat(amt), payment_method: method,
                                payment_date: new Date().toISOString().slice(0, 10),
                              }, { withCredentials: true });
                              toast.success(`Payment of ₹${amt} recorded for ${v.party_name}`);
                              fetchAll();
                            } catch (e) {
                              toast.error('Payment failed: ' + (e.response?.data?.detail || e.message));
                            }
                          }}
                            className="p-1.5 rounded-lg bg-green-900/30 text-green-400 hover:text-green-300 border border-green-700/40"
                            title="Record Payment (also updates cashflow)"
                            data-testid={`voucher-record-payment-${v.id}`}>
                            <CreditCard size={12}/>
                          </button>
                        )}
                        <button onClick={() => downloadPdf(`${API}/vouchers/${v.id}/pdf`, `${v.voucher_type === 'sales_invoice' ? 'Invoice' : 'Voucher'}_${v.voucher_number || v.id}.pdf`)}
                          className="p-1.5 rounded-lg bg-zinc-800 text-amber-400 hover:text-amber-300"
                          title="Download PDF"
                          data-testid={`voucher-pdf-${v.id}`}>
                          <Download size={12}/>
                        </button>
                        {v.status !== 'cancelled' && (
                          <button onClick={() => handleCancel(v.id)} className="p-1.5 rounded-lg bg-zinc-800 text-zinc-400 hover:text-red-400" title="Cancel voucher"><XCircle size={12}/></button>
                        )}
                        {isAdmin && (
                          <button onClick={() => requestDelete(`/vouchers/${v.id}`, v.voucher_no || v.id, `Delete voucher ${v.voucher_no || v.id}. This reverses all accounting entries.`)} className="p-1.5 rounded-lg bg-red-900/30 text-red-400 hover:text-red-300 hover:bg-red-900/50 border border-red-800/30" title="Delete"><Trash2 size={12}/></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-zinc-600">
              <FileText size={40} className="mb-3"/>
              <p className="text-sm">No vouchers found</p>
              <p className="text-xs mt-1">Click "New Voucher" to create one</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick type selector when daybook + clicking new */}
      {showCreate && !selectedType && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#0F0F11] border border-zinc-700 rounded-2xl w-full max-w-2xl p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading font-bold text-zinc-50">Select Voucher Type</h3>
              <button onClick={() => setShowCreate(false)}><X size={18} className="text-zinc-500"/></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {CATEGORIES.map(cat => (
                <div key={cat.id}>
                  <p className={`text-xs font-bold mb-2 ${cat.color}`}>{cat.name}</p>
                  <div className="space-y-1">
                    {cat.types.map(type => (
                      <button key={type.key} onClick={() => { setSelectedType(type); }} className={`w-full text-left px-3 py-2 rounded-xl ${cat.bg} ${cat.border} border text-xs ${cat.color} hover:opacity-80 transition-opacity`}>
                        {type.label}
                        {type.hint && <span className="float-right text-zinc-600">{type.hint}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit voucher form — same component, edit mode passes
          `existingVoucher` so the form preloads + PUTs on submit */}
      {showCreate && selectedType && (
        <VoucherForm type={selectedType} vendors={vendors} materials={materials} products={products} batches={batches} employees={employees} machines={machines} clients={clients}
          onProductsChanged={async () => { try { const r = await axios.get(`${API}/inventory/finished-goods`, { withCredentials: true }); setProducts(r.data); } catch (e) { /* noop */ } }}
          existingVoucher={editingVoucher}
          onSubmit={handleSubmit} onClose={() => { setShowCreate(false); setSelectedType(null); setEditingVoucher(null); }} />
      )}

      {/* Detail view */}
      {showDetail && <VoucherDetail voucher={showDetail} onClose={() => setShowDetail(null)} />}
      {invoiceOrder && <InvoiceModal order={invoiceOrder} onClose={() => setInvoiceOrder(null)} onUpdate={(updated) => { setInvoiceOrder(updated); fetchAll(); }} />}
      {previewOrderId && <BillPrintPreview orderId={previewOrderId} onClose={() => setPreviewOrderId(null)} />}
      <ConfirmDeleteModal open={deleteState.open} onClose={cancelDelete} onConfirm={confirmDelete} title={deleteState.title} message={deleteState.message} />
      <ConfirmDeleteModal open={cancelModal.open} onClose={() => setCancelModal({ open: false, id: null })} onConfirm={confirmCancelVoucher} title="Cancel Voucher?" message="This will mark the voucher as cancelled. This cannot be undone." />
    </div>
  );
}


/**
 * SalesItemCombo — searchable combobox for sales-invoice line items.
 * Lists every finished good (tagged FG, blue) and every raw material
 * (tagged RM, green). Typing filters by name. Picking a row calls back to
 * the parent with the correct setter — keeps backend payload consistent
 * (product_id XOR material_id).
 *
 * Kept lightweight (no popover library) for table-row UX: a small floating
 * <ul> beneath the input opens on focus + when the query has 1+ chars.
 */
function SalesItemCombo({ value, products, materials, onPickProduct, onPickMaterial }) {
  const [q, setQ] = React.useState(value || '');
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => { setQ(value || ''); }, [value]);

  const norm = (s) => (s || '').toLowerCase();
  const term = norm(q.trim());
  // Build a unified list. Show FG first (most common in sales invoices),
  // then RM. Cap at 50 rows so the dropdown stays usable for big inventories.
  const rows = [
    ...products.map(p => ({ kind: 'FG', id: p.id, name: p.name || '', unit: p.unit || '', tagColor: 'bg-blue-100 text-blue-700' })),
    ...materials.map(m => ({ kind: 'RM', id: m.id, name: m.name || '', unit: m.unit || '', tagColor: 'bg-green-100 text-green-700' })),
  ].filter(r => !term || norm(r.name).includes(term)).slice(0, 50);

  const pick = (r) => {
    setQ(r.name);
    setOpen(false);
    if (r.kind === 'FG') onPickProduct(r.id);
    else onPickMaterial(r.id);
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={q}
        placeholder="Type to search products / raw materials…"
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="w-full bg-transparent text-gray-900 focus:outline-none"
        data-testid="sales-item-combo-input"
      />
      {open && rows.length > 0 && (
        <ul
          className="absolute z-50 left-0 right-0 mt-0.5 bg-white border border-gray-300 rounded shadow-lg max-h-56 overflow-y-auto text-[11px]"
          data-testid="sales-item-combo-list"
        >
          {rows.map(r => (
            <li key={`${r.kind}-${r.id}`}
              onMouseDown={() => pick(r)}
              className="px-2 py-1.5 hover:bg-blue-50 cursor-pointer flex items-center gap-2 border-b border-gray-100 last:border-0"
              data-testid={`sales-combo-row-${r.kind}-${r.id}`}>
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${r.tagColor} flex-shrink-0`}>{r.kind}</span>
              <span className="text-gray-900 flex-1 truncate">{r.name}</span>
              {r.unit && <span className="text-gray-500 text-[10px] flex-shrink-0">{r.unit}</span>}
            </li>
          ))}
        </ul>
      )}
      {open && rows.length === 0 && term && (
        <ul className="absolute z-50 left-0 right-0 mt-0.5 bg-white border border-gray-300 rounded shadow text-[11px]">
          <li className="px-2 py-1.5 text-gray-500 italic">No products or materials match "{q}"</li>
        </ul>
      )}
    </div>
  );
}

/**
 * UnifiedBillsTable — sales-invoice list rendered from the SAME
 * `/api/invoices/bill-log` data that Bill Log and Enterprise Bill History
 * use. Numbering = canonical TI/GST/…, so deleting a row here drops the
 * bill everywhere (one source of truth = sales_orders).
 *
 * Render-only — all mutating actions are passed in as callbacks.
 */
function UnifiedBillsTable({ bills, search, sortKey, sortDir, toggleSort, isAdmin, onView, onPreview, onDelete }) {
  const tokens = (search || '').trim().toLowerCase();
  const seq = (s) => parseInt(((s || '').match(/(\d+)$/) || ['0', '0'])[1], 10);
  const cmp = (a, b) => {
    let va, vb;
    switch (sortKey) {
      case 'voucher_number': va = seq(a.invoice_number); vb = seq(b.invoice_number); break;
      case 'amount':         va = parseFloat(a.total_amount || 0); vb = parseFloat(b.total_amount || 0); break;
      case 'party':          va = (a.client || '').toLowerCase(); vb = (b.client || '').toLowerCase(); break;
      case 'status':         va = (a.payment_status || 'Unpaid').toLowerCase(); vb = (b.payment_status || 'Unpaid').toLowerCase(); break;
      case 'reference':      va = (a.buyer_ref || '').toLowerCase(); vb = (b.buyer_ref || '').toLowerCase(); break;
      case 'type':           va = a.gst_enabled ? 1 : 0; vb = b.gst_enabled ? 1 : 0; break;
      case 'date':
      default:               va = a.invoice_date || a.created_at || ''; vb = b.invoice_date || b.created_at || ''; break;
    }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ?  1 : -1;
    return 0;
  };
  const rows = bills
    .filter(b => {
      if (!tokens) return true;
      const inv = (b.invoice_number || '').toLowerCase();
      const tail = inv.match(/(\d+)$/)?.[1] || '';
      const cli = (b.client || '').toLowerCase();
      return inv.includes(tokens) || tail.includes(tokens) || cli.includes(tokens);
    })
    .sort(cmp);

  const HEADERS = [
    { label: 'Date',         key: 'date' },
    { label: 'Invoice No.',  key: 'voucher_number' },
    { label: 'Type',         key: 'type' },
    { label: 'Client',       key: 'party' },
    { label: 'Buyer Ref',    key: 'reference' },
    { label: 'Total',        key: 'amount' },
    { label: 'Payment',      key: 'status' },
    { label: 'Actions',      key: null },
  ];

  return (
    <>
      <table className="w-full text-sm min-w-[720px]" data-testid="unified-bills-table">
        <thead className="sticky top-0 bg-[#0A0A0C] z-10">
          <tr className="border-b border-zinc-800">
            {HEADERS.map(({ label, key }) => {
              const isActive = key && key === sortKey;
              return (
                <th key={label}
                  onClick={key ? () => toggleSort(key) : undefined}
                  className={`text-left text-xs font-medium px-4 py-3 whitespace-nowrap select-none ${key ? 'cursor-pointer hover:text-zinc-200' : ''} ${isActive ? 'text-amber-400' : 'text-zinc-500'}`}
                  data-testid={key ? `unified-sort-${key}` : undefined}>
                  {label}
                  {isActive && <span className="ml-1 text-[10px]">{sortDir === 'asc' ? '↑' : '↓'}</span>}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map(b => (
            <tr key={b.id} className="border-b border-zinc-800/40 hover:bg-zinc-800/20 transition-colors" data-testid={`unified-bill-row-${b.id}`}>
              <td className="px-4 py-3 font-mono text-zinc-400 text-xs">{(b.invoice_date || b.created_at || '').slice(0, 10)}</td>
              <td className="px-4 py-3 font-mono text-amber-400 text-xs font-bold">{b.invoice_number}</td>
              <td className="px-4 py-3">
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${b.gst_enabled ? 'bg-green-900/40 text-green-300 border-green-700' : 'bg-amber-900/40 text-amber-300 border-amber-700'}`}>
                  {b.gst_enabled ? 'GST' : 'Non-GST'}
                </span>
              </td>
              <td className="px-4 py-3 text-zinc-200 text-xs max-w-48 truncate">{b.client}</td>
              <td className="px-4 py-3 font-mono text-zinc-500 text-xs">{b.buyer_ref || '—'}</td>
              <td className="px-4 py-3 font-mono text-green-400 text-xs font-semibold">{fmt(b.total_amount)}</td>
              <td className="px-4 py-3">
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${b.payment_status === 'Paid' ? 'bg-green-900/40 text-green-300 border-green-700' : b.payment_status === 'Partial' ? 'bg-amber-900/40 text-amber-300 border-amber-700' : 'bg-red-900/30 text-red-400 border-red-800'}`}>
                  {b.payment_status || 'Unpaid'}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1">
                  <button onClick={() => onPreview(b.id)}
                    className="p-1.5 rounded-lg bg-zinc-800 text-blue-400 hover:text-blue-300"
                    title="View / Print Preview"
                    data-testid={`unified-view-${b.id}`}>
                    <Eye size={12}/>
                  </button>
                  <button onClick={() => onView(b)}
                    className="p-1.5 rounded-lg bg-green-900/30 text-green-300 hover:text-green-200 border border-green-700/40"
                    title="Send / Record Payment / Attach"
                    data-testid={`unified-share-${b.id}`}>
                    <FileText size={12}/>
                  </button>
                  <button onClick={() => downloadPdf(`${API}/invoices/${b.id}/pdf`, `Invoice_${b.invoice_number || b.id}.pdf`)}
                    className="p-1.5 rounded-lg bg-zinc-800 text-amber-400 hover:text-amber-300"
                    title="Download PDF"
                    data-testid={`unified-pdf-${b.id}`}>
                    <Download size={12}/>
                  </button>
                  {isAdmin && (
                    <button onClick={() => onDelete(b)}
                      className="p-1.5 rounded-lg bg-red-900/30 text-red-400 hover:text-red-300 hover:bg-red-900/50 border border-red-800/30"
                      title="Delete bill (10s undo)"
                      data-testid={`unified-delete-${b.id}`}>
                      <Trash2 size={12}/>
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-zinc-600">
          <FileText size={40} className="mb-3"/>
          <p className="text-sm">No bills in this period</p>
          <p className="text-xs mt-1">Click "+ New Sales Invoice" to raise one</p>
        </div>
      )}
    </>
  );
}

